/**
 * Screen 1e — multi-well correlation.
 *
 * Wells side by side, hung either on measured depth or flattened on a datum
 * marker. Tie lines are drawn in one overlay SVG spanning the whole panel, so
 * they stay continuous across well boundaries as columns resize.
 *
 * Markers are editable in place: drag one, or use its context menu to move it
 * to the cursor, rename it, make it the datum, or push it to every well.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, unpage, type Page } from "../api";
import { canWrite } from "../auth";
import Basemap, {
  BASEMAP_LAYER_DEFAULTS,
  BasemapLayerToggles,
  BasemapLegend,
  type BasemapLayers,
} from "../components/Basemap";
import { ColorPicker } from "../components/ColorPicker";
import { PaletteEditor } from "../components/PaletteEditor";
import { registerUserRamps } from "../lib/colorscale";
import { normalizeUserRamps, type UserRamp } from "../lib/userramps";
import { ScreeningTable } from "../components/DataScope";
import { Icon } from "../components/icons";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import {
  BoxZoomOverlay,
  DEPTH_GUTTER_W,
  DepthGutter,
  LogTrack,
  TrackHeader,
  type TrackSpec,
} from "../components/LogTracks";
import {
  Button,
  Checkbox,
  Chip,
  ContextMenu,
  Dropdown,
  Empty,
  Loading,
  Modal,
  Notice,
  NumberField,
  Panel,
  Segmented,
  Slider,
  TextField,
  Toggle,
  useToast,
} from "../components/ui";
import { useAsync, useDebounced, useElementSize, useReorderDrag, useStored } from "../hooks";
import {
  axisSpan,
  orientRange,
  runsUpward,
  DEPTH_CAPTION,
  DEPTH_LABEL,
  DEPTH_ORDER,
} from "../lib/depthaxis";
import {
  columnBounds,
  tieLinePoints,
  gapFlex,
  CORR_GAPS,
  CORR_GAP_DEFAULT,
} from "../lib/corrspacing";
import {
  depthTickCount,
  DEPTH_SCALE_PRESETS,
  stepScale,
  type ScaleMode,
} from "../lib/depthscale";
import { useDepthSheet } from "../lib/depthsheet";
import { unitsFor } from "../lib/units";
import { chunk, PALETTE, surfaceDistance } from "../lib/correlation";
import {
  corrGroupSpec,
  groupsToCurves,
  keyMnemonic,
  keyWellId,
  mergeIntoTrack,
  normalizeCorrCurves,
  normalizeCorrDisplay,
  CORR_DEFAULT_TRACKS,
  normalizeCorrGroups,
  splitFromTrack,
  patchCorrCurve,
  patchCorrSpectrum,
  removeCorrSpectrum,
  resolveCorrColor,
  toggleCorrScale,
  type CorrCurveStore,
  type CorrDisplay,
} from "../lib/corrtracks";
import { moveItem } from "../lib/tracklayout";
import type { CurveConfig, SpectrumConfig } from "../lib/tracklayout";
import { MarkerDialog, pickedDepth, type MarkerDraft } from "../components/MarkerTools";
import type {
  CorrelationSection,
  DepthReference,
  MapPayload,
  MapWell,
  PanelPayload,
  PanelWell,
  Project,
  ScreenedWell,
  Screening,
  Well,
} from "../types";

const HEADER_H = 46;

/**
 * Isi rel kanan layar ini, sebagai daftar tab yang sama bentuknya dengan rel
 * layar log — satu nama, sebab rel ini memang satu kolom panel dan bukan empat
 * tab. Namanya tetap dicetak menurun ketika relnya terlipat: yang membedakan
 * sebuah pita dari sebuah tepi bukanlah banyaknya nama melainkan bahwa namanya
 * terbaca dan bisa ditekan.
 */
const RAIL_TABS: RailTabItem<"korelasi">[] = [{ key: "korelasi", label: "Correlation" }];

/** Satu larik kosong bersama: `?? []` melahirkan larik baru tiap render. */
const NO_WELLS: MapWell[] = [];

/** Kurva yang harus dibawa sebuah sumur supaya ikut lembar, selama aturannya menyala. */
const REQUIRED_CURVES = ["GR", "RT"];

const AVAILABLE_CURVES = ["GR", "PERM", "PHIE", "RT", "SW", "VSH", "RHOB", "NPHI"];

/** Colours for wells stacked into one panel — deliberately unlike curve colours. */
const WELL_COLORS = [
  "var(--accent)",
  "var(--curve-rt)",
  "var(--green)",
  "var(--violet)",
  "var(--curve-rhob)",
  "var(--curve-nphi)",
];


export default function Correlation({
  project,
  well,
  dataVersion = 0,
}: {
  project: Project;
  well: Well | null;
  /** Changes when the project's curves have been edited elsewhere. */
  dataVersion?: number;
}) {
  const toast = useToast();
  const mayWrite = canWrite(project.my_role);

  const [sectionId, setSectionId] = useState<number | null>(null);
  /**
   * Setelan lembar korelasi: susunan track, lebar spasi, dan setelan tiap
   * mnemonic.
   *
   * Hidup di server pada seksinya, bukan di peramban masing-masing orang.
   * Lembar korelasi dibaca bersama dan dibicarakan bersama — "kotak kedua dari
   * kiri" harus menunjuk kotak yang sama bagi setiap orang yang membukanya —
   * dan warna sumur sudah lama tinggal di sana dengan alasan yang persis sama.
   *
   * Yang tersimpan di peramban tetap dibaca sebagai cadangan, sekali, ketika
   * seksinya belum punya setelan sendiri. Tanpa itu, seorang pengguna yang
   * sudah menyusun lembarnya akan membuka layar ini dan menemukannya kembali
   * ke bawaan, tanpa satu pun kata yang menjelaskan ke mana perginya.
   */
  const [storedGroups] = useStored<string[][] | null>("evolog.corr.tracks", null);
  // Bawaannya null dan bukan `CORR_DEFAULT_TRACKS.flat()`: bentuk lama ini
  // satu mnemonic satu kotak, jadi bawaan yang diratakan ke sana memecah
  // RHOB–NPHI yang sengaja ditumpuk menjadi dua kotak pada setiap lembar yang
  // belum pernah disetel.
  const [legacyCurves] = useStored<string[] | null>("evolog.corr.curves", null);
  const [storedGap] = useStored("evolog.corr.gap", CORR_GAP_DEFAULT);
  const [storedCurves] = useStored<unknown>(
    `evolog.corr.curvecolor.${project.id}`, null);

  /** Setelan yang sedang berlaku. Kosong sampai seksinya termuat. */
  const [display, setDisplay] = useState<CorrDisplay | null>(null);

  // Kurva dan selang yang sedang dibandingkan antar sumur. Disimpan supaya
  // pertanyaan yang sama tidak perlu disusun ulang pada setiap kunjungan:
  // "PHIE di Sand A" adalah pertanyaan yang ditanyakan berhari-hari, bukan
  // sekali. Tetap di peramban: ini pertanyaan seorang pembaca, bukan susunan
  // lembar yang dipandang bersama.
  const [compareCurve, setCompareCurve] = useStored("evolog.corr.compare", "PHIE");
  const [compareZone, setCompareZone] = useStored<string>("evolog.corr.compare.zone", "");
  // Bawaannya digantung pada kedalaman, seperti lembar korelasi cetak: garis
  // ikat yang miring dari sumur ke sumur itulah yang dibaca orang pertama
  // kali, dan meratakannya pada datum adalah pertanyaan susulan — satu klik.
  const [flatten, setFlatten] = useStored("evolog.corr.flatten", false);

  /** Jendela pengelola palet warna. */
  const [paletteOpen, setPaletteOpen] = useState(false);

  /**
   * Palet proyek ini, disemai ke pendaftaran begitu proyeknya termuat.
   *
   * Menimpa singgahan peramban dengan sengaja: yang berlaku adalah palet
   * proyeknya, dan palet yang tertinggal di peramban dari proyek lain akan
   * mewarnai lembar ini dengan warna lapangan yang berbeda.
   */
  useEffect(() => {
    registerUserRamps(normalizeUserRamps(project.palettes));
  }, [project.id, project.palettes]);

  /** Menuliskan palet ke proyeknya, tempat seluruh anggotanya melihatnya. */
  const simpanPalet = useCallback(
    (list: UserRamp[]) => {
      if (!mayWrite) return;
      void api
        .put(`/api/projects/${project.id}/palettes/`, { palettes: list })
        .catch((cause) =>
          toast(cause instanceof Error ? cause.message : String(cause), "error"),
        );
    },
    [project.id, mayWrite, toast],
  );
  const [showLitho, setShowLitho] = useStored("evolog.corr.litho", true);
  const [shading, setShading] = useStored("evolog.corr.shading", true);
  const [paper, setPaper] = useStored("evolog.corr.paper", true);
  // Lebar rel kanan. Berapa ruang yang pantas untuk basemap di puncaknya
  // bergantung pada berapa sumur yang sedang dikorelasikan — sebuah traverse
  // tiga sumur tidak memerlukan lebar yang sama dengan traverse enam belas.
  const [railWidth, setRailWidth] = useStored("evolog.corr.rail", 300);
  // Rel ini pun bisa dilipat, dengan alasan yang sama seperti kedua rel layar
  // log: sesudah lintasan tersusun — sumur mana, datum mana, warna mana —
  // yang dipandang berjam-jam sesudahnya adalah lembar korelasinya, dan 300
  // piksel kendali yang sedang tidak disetel tetap 300 piksel. Disimpan,
  // sebab rel yang terbuka lagi setiap kali layar dibuka memaksa penggunanya
  // melipatnya ulang setiap pagi.
  const [railCollapsed, setRailCollapsed] = useStored("evolog.corr.rail.collapsed", false);
  // Tinggi baris kepala track, diukur dan bukan ditebak. Ia tumbuh bersama
  // jumlah kurva yang tertumpuk dalam satu panel, dan mistar kedalaman serta
  // garis ikat harus mulai persis di bawahnya: satu overlay yang meleset lima
  // piksel menaruh setiap top lima piksel dari tempatnya di seluruh lembar.
  const [headBox, headSize] = useElementSize<HTMLDivElement>();
  // A marker present in only one or two wells produces a long diagonal across
  // the whole panel. The threshold is adjustable; the default is two.
  const [minTieWells, setMinTieWells] = useStored("evolog.corr.mintie", 2);
  const units = unitsFor(project.unit_system);
  // Depth reference. In a deviated well MD, TVD and TVDSS differ widely, and
  // only TVDSS can be compared between wells — every rig floor
  // berbeda tingginya.
  const [depthMode, setDepthMode] = useStored<DepthReference>(
    `evolog.corr.depth.${project.id}`, project.default_depth_reference ?? "md");
  const [perPanel, setPerPanel] = useStored("evolog.corr.perpanel", 1);
  // Setelan kurva menetap di peramban: warna, bentuk garis, ketebalan, kedua
  // ujung skala dan arsirannya adalah selera membaca, dan dua orang boleh
  // berbeda. Warna sumur tidak — ia hidup di server, sebab di lembar korelasi
  // warna sumur sudah menjadi bagian dari pembacaan bersama.
  //
  // Kuncinya kunci lama, yang dulu hanya menyimpan warna. Bentuk lama itu masih
  // terbaca; lihat `normalizeCorrCurves`.

  /**
   * Menyunting satu kurva dari kepala tracknya, di panel mana pun.
   *
   * Setelannya milik mnemonic dan bukan milik panel: sebuah lembar korelasi
   * yang GR-nya hijau di satu sumur dan jingga di sumur sebelahnya bukan lagi
   * perbandingan, dan menyetelnya satu per satu di enam panel adalah pekerjaan
   * yang tak seorang pun minta.
   */
  const editCurve = (name: string, patch: Partial<CurveConfig>) =>
    setCurveCfg(patchCorrCurve(curveCfg, name, patch));

  const setWellColor = async (wellId: number, colour: string) => {
    try {
      await api.patch(`/api/wells/${wellId}/`, { display_color: colour });
      panel.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  // Sumur tanpa GR atau tanpa RT disingkirkan dari lembar sebagai bawaan:
  // keduanya tulang punggung korelasi — litologi dan fluidanya — dan kolom
  // yang kehilangan salah satunya adalah kolom yang tidak bisa dibandingkan
  // dengan tetangganya. Tetap bisa dimatikan, dan sumurnya tetap berdiri di
  // daftar dengan sebabnya.
  const [requireGrRt, setRequireGrRt] = useStored("evolog.corr.require.grrt", true);
  // Jaring radius. Sama seperti di tab Batch layar Analysis, ia **memilih
  // sumur** dan tidak memotong kedalaman: lembar korelasi menggantung log
  // sepenuhnya, dan pita yang memotongnya akan menghapus justru bagian yang
  // dipakai orang untuk menarik garis korelasinya. Pitanya tetap dilaporkan
  // sebagai keterangan — di kedalaman mana tetangga itu benar-benar berdekatan
  // dengan acuannya — dan panelnya mengatakan bahwa ia tidak memotong apa pun.
  const [centerWell, setCenterWell] = useStored<number | null>(
    "evolog.corr.radius.center", null);
  const [radiusKm, setRadiusKm] = useStored("evolog.corr.radius.km", 5);
  const [radiusMode, setRadiusMode] = useStored<"surface" | "trajectory">(
    "evolog.corr.radius.mode", "trajectory");
  // Basemap: lapisan yang menyala mengikuti kebiasaan pembacanya, jadi ia
  // menetap di browser. Permukaan yang dikonturkan tidak — secara asali ia
  // adalah datum seksi, supaya peta dan lembar korelasi berbicara tentang
  // horizon yang sama.
  const [basemapLayers, setBasemapLayers] = useStored<BasemapLayers>(
    "evolog.corr.basemap", BASEMAP_LAYER_DEFAULTS);
  const [basemapSurface, setBasemapSurface] = useState<string>("");
  // `marker` kosong berarti klik kanan jatuh di kolom, bukan di sebuah marker;
  // `md` adalah kedalaman tempat jari menekan, yang hanya berarti pada kasus itu.
  const [menu, setMenu] =
    useState<{ x: number; y: number; wellId: number; marker: string; md?: number } | null>(null);
  // Sumur dan namanya ikut dibawa di dalam draft, bukan dibaca ulang dari
  // `menu`: ContextMenu menutup dirinya sendiri sesudah item ditekan, jadi
  // dialog yang bergantung pada menu itu tidak akan pernah sempat tergambar.
  const [markerDraft, setMarkerDraft] =
    useState<(MarkerDraft & { wellId: number; wellName: string }) | null>(null);
  const [renaming, setRenaming] = useState<{ wellId: number; marker: string } | null>(null);
  const [cursorDepth, setCursorDepth] = useState<number | null>(null);
  // Kedudukan kursor pada sumbu bersama panel — kedalaman terukur bila lembar
  // digantung apa adanya, simpangan terhadap datum bila diratakan. Ia bukan
  // `cursorDepth`: yang itu adalah MD di sumur yang sedang disentuh, dan
  // mencetaknya di mistar yang mengukur simpangan akan menaruh angka ratusan
  // meter dari garisnya sendiri.
  const [cursorAxis, setCursorAxis] = useState<number | null>(null);
  /**
   * Yang sedang diseret: sebuah nama marker, dan sumur asalnya bila ada.
   *
   * `wellId` bernilai null ketika seretan dimulai dari daftar Markers di panel
   * kanan — di sana yang dipegang memang hanya sebuah nama, belum melekat pada
   * sumur mana pun. Kedua gerak itu bermuara pada satu maksud yang sama, jadi
   * keduanya memakai satu muatan dan satu tempat penjatuhan.
   */
  const dragging = useRef<{ marker: string; wellId: number | null } | null>(null);

  /**
   * Muatan seretan hanya hidup selama satu tekan-lepas, dan tidak sedetik pun
   * lebih lama.
   *
   * Setiap penekanan tombol menghapusnya lebih dulu; yang menyusul sesudahnya
   * — pegangan marker atau baris panel Markers — yang mengisinya lagi bila
   * penekanan itu memang jatuh di atas salah satunya. Tanpa penjaga ini
   * seretan yang tidak jadi dijatuhkan di kolom mana pun akan meninggalkan
   * muatannya tetap terisi, dan klik biasa berikutnya di sebuah kolom terbaca
   * sebagai penjatuhan seretan yang sudah lama selesai: sebuah top berpindah,
   * atau marker baru terpasang, tanpa ada yang menyeretnya.
   *
   * Ia mendengarkan pada fase tangkap di `window`, bukan pada `mouseup` yang
   * menggelembung. Pelepasan tombol di bilah kanan tidak pernah sampai ke
   * `window` — panel-panel di sana sendiri bisa diseret dan menelan
   * peristiwanya — sehingga penjaga yang bersandar pada `mouseup` justru diam
   * persis pada kasus yang paling perlu dijaganya. Fase tangkap di `window`
   * adalah yang pertama menerima peristiwa, jadi tidak ada yang bisa
   * mendahuluinya, dan ia berjalan sebelum penangan mana pun sempat mengisi
   * muatannya kembali.
   */
  useEffect(() => {
    const begin = () => {
      dragging.current = null;
    };
    window.addEventListener("mousedown", begin, true);
    return () => window.removeEventListener("mousedown", begin, true);
  }, []);

  const sections = useAsync(
    (signal) =>
      api
        .get<Page<CorrelationSection>>("/api/sections/", { project: project.id }, signal)
        .then((page) => unpage(page, signal)),
    [project.id],
  );

  const section = sections.data?.find((s) => s.id === sectionId) ?? sections.data?.[0] ?? null;

  /**
   * Memuat setelan lembar begitu seksinya diketahui, dan hanya sekali per
   * seksi.
   *
   * Bergantung pada `section?.id` dan bukan pada `section`: objek seksinya
   * lahir baru pada setiap muat ulang daftar, dan efek yang bergantung padanya
   * akan menimpa setelan yang sedang disunting orang dengan setelan yang baru
   * saja ia kirim sendiri — suntingan yang berkedip kembali ke keadaan
   * sebelumnya, sedetik sesudah dilakukan.
   */
  useEffect(() => {
    if (!section) return;
    setDisplay(
      normalizeCorrDisplay(section.display, {
        tracks: normalizeCorrGroups(storedGroups ?? legacyCurves ?? CORR_DEFAULT_TRACKS),
        gap: storedGap,
        curves: normalizeCorrCurves(storedCurves),
      }),
    );
    // Cadangan peramban sengaja tidak menjadi dependensi: ia dibaca sekali,
    // pada saat seksinya dibuka, dan bukan setiap kali nilainya berubah.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id]);

  const trackGroups = display?.tracks ?? [];
  const gap = display?.gap ?? CORR_GAP_DEFAULT;
  const curveCfg = display?.curves ?? {};
  /** Mnemonic lembar ini, urut gambar — untuk pengambilan data dan daftar rel. */
  const curves = useMemo(() => groupsToCurves(trackGroups), [trackGroups]);

  /**
   * Menuliskan setelan yang berubah, ke layar seketika dan ke server tertunda.
   *
   * Seketika di layar sebab yang menggeser sakelar spasi menunggu lembarnya
   * bergerak, bukan menunggu jaringan. Tertunda ke server sebab menyeret
   * pemilih lebar melahirkan selusin keadaan antara, dan menuliskan
   * masing-masing berarti selusin permintaan yang sebelas di antaranya sudah
   * usang sebelum tiba.
   */
  const ubahDisplay = (patch: Partial<CorrDisplay>) => {
    setDisplay((sebelumnya) => (sebelumnya ? { ...sebelumnya, ...patch } : sebelumnya));
  };
  const setTrackGroups = (next: string[][]) =>
    ubahDisplay({ tracks: normalizeCorrGroups(next) });
  const setGap = (next: number) => ubahDisplay({ gap: next });
  const setCurveCfg = (next: CorrCurveStore) => ubahDisplay({ curves: next });

  // Yang dikirim adalah keadaan yang sudah tenang setengah detik.
  const tersimpan = useDebounced(display, 500);
  const terkirim = useRef<string | null>(null);
  useEffect(() => {
    if (!section || !tersimpan) return;
    const teks = JSON.stringify(tersimpan);
    // Muatan pertama bukan suntingan: ia keadaan yang baru saja dibaca dari
    // server, dan mengirimkannya kembali menuliskan cadangan peramban ke setiap
    // seksi yang sekadar dibuka.
    if (terkirim.current === null) {
      terkirim.current = JSON.stringify(
        normalizeCorrDisplay(section.display, tersimpan),
      );
      if (terkirim.current === teks) return;
    }
    if (terkirim.current === teks) return;
    terkirim.current = teks;
    // Peran hanya-baca tetap boleh menyusun lembarnya untuk dirinya sendiri;
    // yang tidak boleh adalah menuliskannya untuk orang lain.
    if (!mayWrite) return;
    void api
      .patch(`/api/sections/${section.id}/`, { display: tersimpan })
      .catch((cause) =>
        toast(cause instanceof Error ? cause.message : String(cause), "error"),
      );
    // `section` sengaja tidak menjadi dependensi; lihat efek pemuatan di atas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tersimpan, mayWrite]);

  // The traverse map: surface positions of every well in the project, so the
  // panel order reads as a path across the field and not just a row of columns.
  const map = useAsync(
    (signal) => api.get<MapPayload>(`/api/projects/${project.id}/map/`, {}, signal),
    [project.id],
  );

  // GR dan RT selalu diminta selama aturannya menyala, juga ketika lembarnya
  // tidak menggambar salah satunya: tanpa itu tidak ada yang bisa mengatakan
  // sumur mana yang tidak membawanya.
  const fetched = useMemo(
    () => (requireGrRt ? [...new Set([...curves, ...REQUIRED_CURVES])] : curves),
    [curves, requireGrRt],
  );

  const panel = useAsync(
    (signal) =>
      api.get<PanelPayload>(
        `/api/sections/${section!.id}/panel/`,
        { curves: fetched.join(","), decimate: 800, depth: depthMode },
        signal,
      ),
    [section?.id, fetched.join(","), depthMode, dataVersion],
    { skip: !section },
  );

  /**
   * Sumur yang disingkirkan aturan GR–RT, dengan kurva yang tidak dibawanya.
   *
   * Kosong bila **semua** sumur kekurangan: lembar tanpa satu sumur pun bukan
   * keadaan kosong yang menjelaskan dirinya melainkan layar putih, dan sebuah
   * lapangan yang memang belum punya RT lebih baik dilihat tanpa RT daripada
   * tidak dilihat sama sekali.
   */
  const lacking = useMemo(() => {
    const out = new Map<number, string[]>();
    if (!requireGrRt) return out;
    const wells = panel.data?.wells ?? [];
    for (const w of wells) {
      const gone = REQUIRED_CURVES.filter((name) => !w.curves[name]);
      if (gone.length) out.set(w.id, gone);
    }
    return out.size === wells.length ? new Map<number, string[]>() : out;
  }, [panel.data, requireGrRt]);

  const visibleWells = useMemo(
    () => (panel.data?.wells ?? []).filter((w) => !hidden.has(w.id) && !lacking.has(w.id)),
    [panel.data, hidden, lacking],
  );

  // Yang dijaring adalah sumur seksi ini, bukan seluruh lapangan: tabel yang
  // menyebut sumur yang tidak ada di lembarnya menjawab pertanyaan yang tidak
  // sedang ditanyakan.
  const sectionWellIds = useMemo(
    () => (panel.data?.wells ?? []).map((w) => w.id).join(","),
    [panel.data],
  );
  const screening = useAsync(
    (signal) =>
      api.get<Screening>(
        `/api/projects/${project.id}/screening/`,
        {
          wells: sectionWellIds,
          center_well: centerWell,
          radius: Math.round(radiusKm * 1000),
          radius_mode: radiusMode,
        },
        signal,
      ),
    [project.id, sectionWellIds, centerWell, radiusKm, radiusMode],
    { skip: centerWell === null || !sectionWellIds },
  );
  const keadaan = useMemo(() => {
    const rows = new Map<number, ScreenedWell>();
    for (const row of screening.data?.wells ?? []) rows.set(row.well_id, row);
    return rows;
  }, [screening.data]);
  const terjaring = useMemo(
    () => (screening.data?.wells ?? [])
      .filter((row) => row.state !== "outside radius" && row.state !== "no coordinates")
      .map((row) => row.well_id),
    [screening.data],
  );

  /** Nama zona yang dipunyai sumur mana pun di panel, urut sebagaimana adanya. */
  const zoneNames = useMemo(() => {
    const seen: string[] = [];
    for (const w of visibleWells) {
      for (const zone of w.zones) if (!seen.includes(zone.name)) seen.push(zone.name);
    }
    return seen;
  }, [visibleWells]);

  /**
   * Ringkasan satu kurva per sumur, di dalam selang yang sama.
   *
   * Satuan bandingnya zona dan bukan jendela kedalaman: sebuah reservoir duduk
   * lebih dalam di satu sumur daripada di sumur sebelahnya, dan membandingkan
   * 2.070–2.090 m di kelimanya berarti membandingkan lapisan yang berbeda lalu
   * menyebutnya satu lapisan. Tiap sumur menyumbang cuplikan di antara puncak
   * dan dasar zonanya sendiri.
   *
   * Sumur yang tidak punya zona itu tidak dikarang nilainya — ia memang tidak
   * menjawab pertanyaannya, dan baris kosong mengatakannya.
   */
  const comparison = useMemo(() => {
    if (!panel.data) return [];
    return visibleWells.map((w) => {
      const curve = w.curves[compareCurve];
      const zone = compareZone ? w.zones.find((z) => z.name === compareZone) : null;
      const missingZone = Boolean(compareZone) && !zone;
      const inside: number[] = [];
      if (curve && !missingZone) {
        curve.md.forEach((md, index) => {
          if (zone && (md < zone.top || md > zone.base)) return;
          const value = curve.values[index];
          if (value !== null && Number.isFinite(value)) inside.push(value);
        });
      }
      // Ringkasan run: tebal net dan rerata berbobot net-pay, dihitung mesinnya
      // pada resolusi penuh. Yang ada di sini hanya untuk zona bernama, sebab
      // itulah satuan yang dipakai perhitungan sumatif.
      const summary = compareZone ? w.zone_summary?.[compareZone] : undefined;
      const NET_AVERAGE: Record<string, number | null | undefined> = {
        PHIE: summary?.phie_avg,
        SW: summary?.sw_avg,
        VSH: summary?.vsh_avg,
        PERM: summary?.perm_avg,
      };
      const netMean = NET_AVERAGE[compareCurve];
      return {
        id: w.id,
        name: w.name,
        color: w.panel_color || w.display_color,
        missingCurve: !curve,
        missingZone,
        undrilled: w.status === "planned",
        // Zona yang ada tetapi tidak tersentuh log sama sekali. KTI-22 punya
        // Menggala Sand C di 2260–2282 m sementara lognya berhenti di 2259,9,
        // dan barisnya dulu berbunyi "zona tak ada" — zonanya ada, lognya yang
        // tidak sampai, dan keduanya menuntut tindakan yang berbeda.
        beyondLog: Boolean(zone) && !summary && inside.length === 0,
        thickness: summary ? summary.gross : zone ? zone.base - zone.top : null,
        // Zona yang menjulur ke luar selang terhitung dilaporkan mesinnya
        // sebatas yang tertutup: KTI-09 punya Sand A 2052–2071 m sementara
        // lognya baru mulai di 2060, jadi net-nya diukur atas 11 m dan bukan
        // 19. Membandingkan 11 m melawan 19 m tanpa mengatakannya adalah
        // perbandingan yang salah dan terlihat benar.
        clipped:
          Boolean(zone && summary && zone.base - zone.top - summary.gross > 0.5),
        nominal: zone ? zone.base - zone.top : null,
        net: summary ? summary.net_reservoir : null,
        netPay: summary ? summary.net_pay : null,
        ntg: summary ? summary.ntg : null,
        n: inside.length,
        // Rerata di atas cutoff bila mesinnya menyimpannya untuk kurva ini;
        // kalau tidak, rerata kasar dari cuplikan panel — dan keterangan di
        // bawah menyebutkan yang mana, sebab keduanya menjawab pertanyaan yang
        // berbeda dan hanya berbeda beberapa perseratus.
        mean: netMean ?? (inside.length
          ? inside.reduce((a, b) => a + b, 0) / inside.length
          : null),
        overCutoff: netMean !== null && netMean !== undefined,
        unit: curve?.unit ?? "",
      };
    });
  }, [panel.data, visibleWells, compareCurve, compareZone]);

  /** Benar bila baris-barisnya memang menyebut rerata di atas cutoff. */
  const netWeighted = comparison.some((row) => row.overCutoff);

  /**
   * Shared depth window.
   *
   * Flattened, every well is drawn relative to its own datum, so the window is
   * an offset range; unflattened it is the union of the wells' MD ranges.
   */
  const depthWindow = useMemo<[number, number] | null>(() => {
    if (!visibleWells.length) return null;
    // Arah sumbunya milik muatan, bukan pemilihnya: `depthMode` sudah berubah
    // sementara panelnya masih menggambar acuan sebelumnya.
    const mode = visibleWells[0].depth_mode ?? "md";
    if (flatten) {
      // Jepitannya dinyatakan sebagai "140 m di atas datum, 160 m di bawah" —
      // sebuah arah, bukan sebuah tanda. Pada ELEV tandanya terbalik, jadi
      // yang dijepit adalah simpangan yang sudah dijadikan positif-ke-bawah,
      // lalu hasilnya dikembalikan ke arah sumbunya.
      const sign = runsUpward(mode) ? -1 : 1;
      const below = visibleWells.flatMap((w) =>
        w.datum_md === null || !w.md_range
          ? []
          : [(w.md_range[0] - w.datum_md) * sign, (w.md_range[1] - w.datum_md) * sign],
      );
      if (!below.length) return null;
      const top = Math.max(Math.min(...below), -140);
      const bottom = Math.min(Math.max(...below), 160);
      return [top * sign, bottom * sign];
    }
    // Marker ikut membentangkan jendelanya. Jendela yang hanya mengikuti
    // rentang kurva memotong setiap top di bawah log terakhir — basement,
    // TD formasi — dan garis ikatnya lenyap tanpa satu kata pun, sementara
    // markernya ada di sumur dan terdaftar di panel Markers.
    const depths = visibleWells.flatMap((w) => [
      ...(w.md_range ? w.md_range : []),
      ...w.markers.map((m) => m.md).filter(Number.isFinite),
    ]);
    if (!depths.length) return null;
    return orientRange(Math.min(...depths), Math.max(...depths), mode);
  }, [visibleWells, flatten]);

  const depthSpan = depthWindow ? axisSpan(depthWindow[0], depthWindow[1]) : 0;
  /** Tinggi yang ditempati kepala panel dan kepala track bersama-sama. */
  const headerH = HEADER_H + headSize.height;

  /**
   * Roda, mistar dan kotak — tangan yang sama dengan lembar log.
   *
   * Kepala sumur dan kepala track ikut bergulir di dalam panel ini dan hanya
   * menempel di puncaknya, jadi tingginya diserahkan sebagai `headerH`:
   * kedalaman di puncak panel bukan kedalaman yang tergambar di sana. Seretan
   * yang lahir di dalam kepala itu bukan pilihan interval melainkan setelan
   * kurva, dan `skipBoxOn` yang menyisihkannya.
   */
  const { attachPane, sheet, setScaleMode, stretch, boxHandlers, box } =
    useDepthSheet({
      storageKey: "evolog.corr.scale",
      metres: depthSpan,
      range: depthWindow,
      headerH,
      skipBoxOn: "[data-corr-head]",
    });

  /**
   * Cetakan sebuah angka pada sumbu bersama.
   *
   * Diratakan pada datum, angkanya adalah simpangan dan tandanya bagian dari
   * bacaan: "+40" berarti empat puluh meter di bawah datum, dan "40" tanpa
   * tanda terbaca sebagai kedalaman empat puluh meter.
   */
  const axisLabel = (value: number) =>
    flatten && value >= 0 ? `+${value.toFixed(0)}` : value.toFixed(0);

  /** Depth (or datum offset) → fraction of the panel height. */
  const toFraction = (value: number) => {
    if (!depthWindow) return 0;
    return (value - depthWindow[0]) / (depthWindow[1] - depthWindow[0] || 1);
  };

  /** Where a marker sits in the shared window for a given well. */
  const markerFraction = (panelWell: PanelWell, md: number): number | null => {
    if (!depthWindow) return null;
    if (flatten) {
      if (panelWell.datum_md === null) return null;
      return toFraction(md - panelWell.datum_md);
    }
    return toFraction(md);
  };

  /**
   * Tie lines, in a 1000 × 1000 viewBox rather than pixels.
   *
   * Columns are equal-width `flex: 1`, so a well's centre is simply
   * (index + 0.5) / count. Drawing in that abstract space and stretching it
   * with `preserveAspectRatio="none"` keeps the lines glued to the columns
   * through any resize, with no measurement to go stale.
   */
  const allWells = panel.data?.wells ?? [];
  // Wells converted on an assumed-vertical trajectory are named. Assuming
  // vertical in silence is the easiest way to put a marker in a deviated well
  // hundreds of metres out with nobody noticing.
  const assumedVertical = allWells
    .filter((w) => w.depth_source === "vertical")
    .map((w) => w.name);
  const total = allWells.length;
  // Permukaan yang dikonturkan basemap: pilihan pengguna bila ada, datum seksi
  // bila tidak, dan marker pertama yang ada bila seksi ini pun belum berdatum.
  const basemapMarker =
    basemapSurface || section?.datum_marker || panel.data?.marker_names[0] || "";
  // Rujukan yang stabil. Set atau larik yang lahir baru tiap render membuat
  // basemap membangun ulang kisi konturnya pada setiap ketikan di layar ini.
  const basemapWells = map.data?.wells ?? NO_WELLS;
  const basemapActiveIds = useMemo(
    () => new Set(visibleWells.map((w) => w.id)),
    [visibleWells],
  );
  const basemapOrder = useMemo(() => visibleWells.map((w) => w.name), [visibleWells]);

  /**
   * Panel-panel lembar ini — satu kolom tergambar per panel, bukan per sumur.
   *
   * Dipisahkan menjadi memo tersendiri sebab tiga tempat memerlukannya dan
   * ketiganya harus sepakat: yang menggambar kolomnya, yang menaruh spasi di
   * antaranya, dan yang menghitung tempat garis ikat menyeberang.
   */
  const panels = useMemo(() => chunk(visibleWells, perPanel), [visibleWells, perPanel]);
  /** Tepi kiri dan kanan tiap kolom, sudah memperhitungkan spasinya. */
  const bounds = useMemo(() => columnBounds(panels.length, gap), [panels.length, gap]);

  const tieLines = useMemo(() => {
    if (!panel.data || !depthWindow || panels.length < 2) return [];
    const colors = ["var(--accent)", "var(--amber)", "var(--blue)", "var(--violet)", "var(--rose)"];
    return panel.data.marker_names.map((name, index) => {
      const stops: { start: number; end: number; y: number }[] = [];
      // Satu titik per kolom, bukan per sumur. Sebelumnya kolomnya dihitung
      // sebagai `(sumur + 0,5) / jumlah sumur`, yang benar hanya selama satu
      // panel berisi satu sumur. Pada "dua sumur per panel" jumlah kolom
      // tinggal separuhnya sementara pembaginya tidak, jadi setiap titik ikat
      // meleset ke kiri — makin jauh makin ke kanan lembar — dan yang terlihat
      // adalah marker yang seolah salah pasang di sumur-sumur terakhir.
      panels.forEach((group, column) => {
        // Sumur pertama panel ini yang punya marker tersebut mewakilinya:
        // kolomnya satu, jadi titiknya pun satu. Menaruh satu titik per sumur
        // bertumpuk berarti beberapa titik pada absis yang sama, dan garis
        // yang melompat tegak lurus di dalam satu kolom.
        for (const panelWell of group) {
          const marker = panelWell.markers.find((m) => m.name === name);
          if (!marker) continue;
          // Top di luar jendela tetap mendapat titiknya. Membuangnya membuat
          // garis melompati sumur itu — atau lenyap sama sekali bila tinggal
          // satu sumur yang tersisa — padahal markernya ada. SVG-nya memotong
          // apa pun di luar kotaknya, jadi garisnya keluar dari tepi lembar
          // ke arah top itu, dan itulah yang benar.
          const fraction = markerFraction(panelWell, marker.md);
          if (fraction === null || !Number.isFinite(fraction)) continue;
          const [start, end] = bounds[column];
          stops.push({ start: start * 1000, end: end * 1000, y: fraction * 1000 });
          break;
        }
      });
      const points = tieLinePoints(stops);
      const drawn = stops.length >= Math.max(2, minTieWells);
      return {
        name,
        color: colors[index % colors.length],
        wells: stops.length,
        // Ujung kiri dan kanan yang **terlihat**, tempat namanya ditulis.
        // Ujung yang keluar dari lembar akan menulis namanya di luar kotak,
        // tempat tak seorang pun bisa membacanya.
        ends: drawn ? visibleEnds(points) : null,
        d: drawn
          ? `M${points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L")}`
          : "",
      };
    });
  }, [panel.data, depthWindow, panels, bounds, flatten, minTieWells]);

  /**
   * Marker disunting pada MD, sebab MD yang disimpan.
   *
   * Menyeretnya di atas sumbu turunan akan menuliskan angka sumbu itu sebagai
   * MD: pada Teapot Dome — meja putar 1,6 km di atas laut — satu seretan
   * memindahkan sebuah top sejauh dua kali tinggi meja putarnya, dan yang
   * tersimpan tetap terbaca sebagai kedalaman yang sah. Menamai, mewarnai dan
   * menghapus tidak menyentuh kedalaman, jadi keduanya tetap terbuka.
   */
  const mayPickDepth = mayWrite && depthMode === "md";

  const moveMarker = async (wellId: number, markerName: string, md: number) => {
    const panelWell = panel.data?.wells.find((w) => w.id === wellId);
    const marker = panelWell?.markers.find((m) => m.name === markerName);
    if (!marker) return;
    try {
      await api.patch(`/api/markers/${marker.id}/`, { md });
      panel.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  /** Warna yang sudah dipakai nama ini di sumur lain, supaya tetap satu warna. */
  const colorOfMarker = (name: string) =>
    panel.data?.wells.flatMap((w) => w.markers).find((m) => m.name === name)?.color ??
    PALETTE[0];

  /**
   * Menjatuhkan seretan pada sebuah kolom.
   *
   * Dua hal bisa terjadi, dan keduanya satu kalimat yang sama — "taruh top ini
   * pada kedalaman ini di sumur ini". Bila sumurnya sudah punya marker
   * bernama itu, marker itu yang dipindah; bila belum, marker itu dipasang di
   * sana. Menjatuhkan ke kolom sumur lain karena itu bukan lagi gerak yang
   * hilang tak berbekas, melainkan cara membawa satu top menyeberang.
   *
   * Marker yang lahir dari seretan diberi keyakinan "sedang". Ia memang
   * diletakkan dengan tangan sambil memandang kurva di panel — jadi bukan
   * tebakan buta seperti "pasang di semua sumur", yang menulis "rendah" — tapi
   * ia juga hasil korelasi dari sumur sebelah, bukan top yang dipilih di atas
   * rangkaian log sumur itu sendiri, jadi belum pantas disebut "tinggi".
   */
  const dropMarker = async (wellId: number, depth: number) => {
    const drag = dragging.current;
    dragging.current = null;
    if (!drag || !mayPickDepth) return;
    const panelWell = panel.data?.wells.find((w) => w.id === wellId);
    if (!panelWell) return;

    const md = pickedDepth(depth);
    const existing = panelWell.markers.find((m) => m.name === drag.marker);
    if (existing) {
      if (Math.abs(existing.md - md) > 1e-6) await moveMarker(wellId, drag.marker, md);
      return;
    }
    try {
      await api.post("/api/markers/", {
        well: wellId,
        name: drag.marker,
        md,
        color: colorOfMarker(drag.marker),
        confidence: "medium",
        source: "log",
      });
      toast(`${drag.marker} set on ${panelWell.name} at ${md.toFixed(1)} m.`);
      panel.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  const setDatum = async (markerName: string) => {
    if (!section) return;
    try {
      await api.patch(`/api/sections/${section.id}/`, { datum_marker: markerName });
      toast(`Datum changed to ${markerName}.`);
      sections.reload();
      panel.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  const applyToAll = async (markerName: string, md: number) => {
    if (!section) return;
    try {
      const result = await api.post<{ created: number[] }>("/api/markers/apply-to-wells/", {
        project: project.id,
        name: markerName,
        md,
        wells: panel.data?.wells.map((w) => w.id) ?? [],
      });
      toast(`${markerName} added to ${result.created.length} wells.`);
      panel.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  /**
   * Menyusun ulang panel sumur, dan menyimpannya di lintasannya.
   *
   * Urutannya milik lintasan, bukan milik peramban yang menyeretnya: lembar
   * korelasi dibaca mendatar dan dibicarakan bersama, dan "yang ketiga dari
   * kiri" harus menunjuk sumur yang sama bagi semua orang yang membukanya.
   * Alasan yang sama membuat warna panel sumur pun disimpan di server.
   *
   * Yang berpindah adalah seluruh isi panel, bukan satu sumur: pada "dua sumur
   * per panel", sebuah panel adalah pasangan yang sengaja ditumpuk, dan
   * memindahkan salah satunya saja membubarkan pasangan itu tanpa diminta.
   */
  const reorderPanels = async (from: number, to: number) => {
    if (!section) return;
    const panels = chunk(visibleWells, perPanel);
    const moved = moveItem(panels, from, to);
    if (moved === panels) return;
    const order = moved.flat().map((well) => well.id);
    try {
      await api.post(`/api/sections/${section.id}/order/`, { wells: order });
      panel.reload();
      sections.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  // Seretan kepala panel: kotaknya adalah panel itu sendiri, pegangannya
  // kepalanya — sebab menyeret lembarnya berarti memilih interval.
  const panelDrag = useReorderDrag((from, to) => void reorderPanels(from, to));

  const deleteMarker = async (wellId: number, markerName: string) => {
    const panelWell = panel.data?.wells.find((w) => w.id === wellId);
    const marker = panelWell?.markers.find((m) => m.name === markerName);
    if (!marker) return;
    try {
      await api.delete(`/api/markers/${marker.id}/`);
      toast(`${markerName} deleted from ${panelWell?.name}.`);
      panel.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  if (sections.loading) return <Loading label="Loading sections…" />;
  if (!sections.data?.length) {
    return (
      <Empty>
        This project has no correlation section yet.
        <span style={{ font: "400 11px var(--font-mono)" }}>
          Open the Map screen, pick the “Draw traverse” tool, then click wells in order.
        </span>
      </Empty>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Tool row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          height: 38,
          flex: "none",
          padding: "0 12px",
          background: "var(--toolbar)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <span className="label">Correlation tools</span>
        <Chip active={flatten} onClick={() => setFlatten(!flatten)}>
          {Icon.datum({ size: 12 })} Flatten on the datum
        </Chip>
        <Chip onClick={() => panel.reload()}>{Icon.markerAdd({ size: 12 })} Reload</Chip>
        <Segmented
          value={String(gap)}
          options={CORR_GAPS.map((g) => ({ value: String(g.value), label: g.label }))}
          onChange={(value) => setGap(Number(value))}
          size="sm"
        />
        <span style={{ flex: 1 }} />
        <Dropdown
          value={section?.id ?? 0}
          options={(sections.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
          onChange={setSectionId}
          width={220}
          compact
        />
        <Dropdown
          value={section?.datum_marker ?? ""}
          label="Datum"
          options={(panel.data?.marker_names ?? []).map((name) => ({ value: name, label: name }))}
          onChange={setDatum}
          width={150}
          compact
          disabled={!mayWrite}
        />
      </div>

      <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
        {/* Hanya lembar korelasinya yang bergulir. Rel kanan berada di luar
            wadah ini: basemap dan daftar sumur adalah kendali lembar itu, dan
            kendali yang hanyut ke atas begitu lembarnya diregangkan tidak bisa
            dipakai justru pada saat paling dibutuhkan. */}
        <div
          ref={attachPane}
          className="scroll-y"
          style={{
            display: "flex",
            alignItems: "stretch",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
        {/* Mistar kedalaman — mistar yang sama dengan lembar log, dan seretnya
            berarti hal yang sama: meregang dan memampatkan skala. Sel kosong
            di puncaknya menempel sebagaimana kepala sumur di sebelahnya
            menempel, sebab mistar yang terus bergulir di bawah kepala yang
            diam mencetak kedalaman yang tidak sejajar dengan apa pun. */}
        <div
          style={{
            width: DEPTH_GUTTER_W,
            flex: "none",
            background: "var(--canvas-gutter)",
            borderRight: "1px solid var(--line)",
            display: "flex",
            flexDirection: "column",
            height: sheet.fitted ? "100%" : headerH + sheet.heightPx,
          }}
        >
          <div
            style={{
              height: headerH,
              flex: "none",
              position: "sticky",
              top: 0,
              zIndex: 6,
              background: "var(--titlebar)",
              borderBottom: "1px solid var(--line)",
            }}
          />
          {depthWindow && (
            <DepthGutter
              top={depthWindow[0]}
              base={depthWindow[1]}
              width={DEPTH_GUTTER_W}
              ticks={depthTickCount(sheet.heightPx)}
              cursor={cursorAxis}
              // Satu desimal untuk kursor, bulat untuk tera: yang dibaca di
              // bawah jari adalah sebuah batas lapisan, dan setengah meter
              // masih berarti pada 1:50.
              cursorLabel={(value) =>
                flatten && value >= 0 ? `+${value.toFixed(1)}` : value.toFixed(1)
              }
              label={axisLabel}
              onStretch={stretch}
              style={{ flex: 1, borderRight: "none" }}
            />
          )}
        </div>

        {/* Well columns */}
        <div
          className={paper ? "corr-paper" : undefined}
          // Seret tegak di atas lembar memilih interval yang hendak dibaca,
          // sebagaimana di lembar log. Seretan yang dimulai di atas sebuah
          // marker tidak sampai ke sini: marker menghentikan peristiwanya
          // sendiri, sebab seretan itu memindahkannya.
          {...boxHandlers}
          style={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            display: "flex",
            background: "var(--canvas)",
            height: sheet.fitted ? "100%" : headerH + sheet.heightPx,
          }}
        >
          {panel.loading && <Loading label="Building panels…" />}
          {panel.error && (
            <div style={{ padding: 16 }}>
              <Notice tone="error">{panel.error}</Notice>
            </div>
          )}

          {panels.map((group, groupIndex, groups) => (
            <Fragment key={group.map((w) => w.id).join("-")}>
            {groupIndex > 0 && gapFlex(gap) > 0 && (
              <PanelGap
                flex={gapFlex(gap)}
                headerH={headerH}
                // Jarak dituliskan di ruang yang memisahkan kedua sumur, dan
                // bukan lagi di dalam kepala salah satunya: sebuah angka yang
                // duduk di kepala sumur kiri terbaca sebagai milik sumur itu,
                // padahal yang diukurnya adalah ruang di sebelah kanannya.
                label={
                  groups[groupIndex - 1] && group[0]
                    ? units.formatDistance(
                        surfaceDistance(
                          groups[groupIndex - 1][groups[groupIndex - 1].length - 1],
                          group[0],
                        ),
                      )
                    : null
                }
              />
            )}
            <WellColumn
              wells={group}
              panelDrag={
                mayWrite
                  ? {
                      boxProps: panelDrag.boxProps(groupIndex),
                      handleProps: panelDrag.handleProps,
                      dragging: panelDrag.dragging === groupIndex,
                      dropEdge:
                        panelDrag.dragging === null ||
                        panelDrag.dropIndex !== groupIndex ||
                        panelDrag.dragging === groupIndex
                          ? null
                          : panelDrag.dropIndex < panelDrag.dragging
                            ? "left"
                            : "right",
                    }
                  : undefined
              }
              swatch={
                group[0].display_color || WELL_COLORS[groupIndex % WELL_COLORS.length]
              }
              wellColor={(w, index) =>
                w.display_color || WELL_COLORS[index % WELL_COLORS.length]
              }
              curveCfg={curveCfg}
              onCurveEdit={editCurve}
              onCurveAuto={(name) => editCurve(name, { min: undefined, max: undefined })}
              onSpectrumEdit={(name, patch) =>
                setCurveCfg(patchCorrSpectrum(curveCfg, name, patch))
              }
              onSpectrumRemove={(name) => setCurveCfg(removeCorrSpectrum(curveCfg, name))}
              onScaleToggle={(name) => setCurveCfg(toggleCorrScale(curveCfg, name))}
              onWellColor={mayWrite ? (id, colour) => void setWellColor(id, colour) : undefined}
              // Hanya panel pertama yang diukur. Semua panel membawa track yang
              // sama, jadi tingginya sama pula — dan mengukur enam untuk
              // mendapat satu angka yang sama enam kali hanya menambah
              // pengamat yang berbunyi bersamaan pada setiap perubahan lebar.
              headRef={groupIndex === 0 ? headBox : undefined}
              units={units}
              // Chip jarak di kepala sumur hanya selama tidak ada spasi untuk
              // menuliskannya. Dua tempat yang mencetak angka yang sama pada
              // lembar yang sama membuat pembacanya mencari perbedaan yang
              // tidak ada.
              distanceToNext={
                gapFlex(gap) === 0 && groups[groupIndex + 1]
                  ? surfaceDistance(group[group.length - 1], groups[groupIndex + 1][0])
                  : null
              }
              groups={trackGroups}
              // Yang berpindah adalah seluruh kotak, bukan satu mnemonic di
              // dalamnya: sebuah track yang memuat GR dan VSH ditumpuk begitu
              // supaya dibaca bersama, dan memindahkan salah satunya saja
              // membubarkan tumpukan itu tanpa diminta.
              onReorder={(name, targetName) => {
                const from = trackGroups.findIndex((g) => g.includes(name));
                const to = trackGroups.findIndex((g) => g.includes(targetName));
                if (from >= 0 && to >= 0) setTrackGroups(moveItem(trackGroups, from, to));
              }}
              window={depthWindow}
              flatten={flatten}
              showLitho={showLitho}
              shading={shading}
              active={group.some((w) => w.id === well?.id)}
              markerFraction={markerFraction}
              onHide={() => setHidden(new Set([...hidden, ...group.map((w) => w.id)]))}
              onMarkerMenu={(event, markerName) =>
                setMenu({ x: event.clientX, y: event.clientY, wellId: group[0].id, marker: markerName })
              }
              onMarkerDragStart={(markerName) => {
                if (mayPickDepth) dragging.current = { marker: markerName, wellId: group[0].id };
              }}
              onColumnMenu={(event, depth) =>
                setMenu({
                  x: event.clientX,
                  y: event.clientY,
                  wellId: group[0].id,
                  marker: "",
                  md: pickedDepth(depth),
                })
              }
              onDepthHover={(depth, axis) => {
                setCursorDepth(depth);
                setCursorAxis(axis);
              }}
              onDrop={(depth) => void dropMarker(group[0].id, depth)}
            />
            </Fragment>
          ))}

          {/* Tie lines */}
          {tieLines.length > 0 && (
            <svg
              viewBox="0 0 1000 1000"
              preserveAspectRatio="none"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: headerH,
                height: `calc(100% - ${headerH}px)`,
                width: "100%",
                pointerEvents: "none",
                // Titik ikat di luar jendela sengaja tetap ada; kotak inilah
                // yang memotongnya di tepi lembar.
                overflow: "hidden",
              }}
              aria-label="Correlation lines"
            >
              {tieLines.map((line) =>
                line.d ? (
                  <path
                    key={line.name}
                    d={line.d}
                    fill="none"
                    stroke={line.color}
                    strokeWidth={1.4}
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null,
              )}
            </svg>
          )}

          {/* Nama marker di kedua ujung garis ikatnya, seperti lembar korelasi
              cetak: garis yang menyeberangi enam sumur tanpa nama harus
              ditelusuri balik ke panel Markers untuk tahu top apa itu. HTML
              dan bukan <text> di SVG di atas: SVG itu direntangkan tanpa
              menjaga rasio, dan hurufnya akan ikut gepeng. */}
          {tieLines.some((line) => line.ends) && (
            <div
              aria-label="Correlation line names"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: headerH,
                height: `calc(100% - ${headerH}px)`,
                pointerEvents: "none",
                overflow: "hidden",
              }}
            >
              {tieLines.map((line) =>
                line.ends
                  ? line.ends.map(([x, y], end) => (
                      <span
                        key={`${line.name}-${end}`}
                        style={{
                          position: "absolute",
                          left: `${x / 10}%`,
                          top: `${y / 10}%`,
                          transform: end === 0 ? "translate(3px, -100%)" : "translate(calc(-100% - 3px), -100%)",
                          padding: "0 3px",
                          background: "var(--canvas)",
                          color: line.color,
                          font: "600 9.5px var(--font-mono)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {line.name}
                      </span>
                    ))
                  : null,
              )}
            </div>
          )}

          {/* Kotak zoom yang sedang diseret, digeser turun setinggi kepala:
              ia diukur dalam piksel isi lembar, dan lembar itu mulai di bawah
              kepala sumur dan kepala track. */}
          {box && depthWindow && (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: headerH,
                height: `calc(100% - ${headerH}px)`,
                pointerEvents: "none",
              }}
            >
              <BoxZoomOverlay
                from={box.from}
                to={box.to}
                height={sheet.heightPx}
                top={depthWindow[0]}
                base={depthWindow[1]}
                format={axisLabel}
              />
            </div>
          )}
        </div>
        </div>

        {/* Right rail */}
        {/* Pegangan lebarnya hanya ada selama ada yang bisa dilebarkan: sebuah
            pemisah yang bisa diseret di tepi pita selebar 30 piksel
            menjanjikan lebar yang tidak akan tampak sampai relnya dibuka. */}
        {!railCollapsed && (
          <Resizer
            width={railWidth}
            onChange={setRailWidth}
            min={260}
            max={560}
            label="Resize the correlation panel"
          />
        )}
        {railCollapsed ? (
          <RailStrip
            tabs={RAIL_TABS}
            active="korelasi"
            onSelect={() => setRailCollapsed(false)}
            expandLabel="Show the correlation panel"
          />
        ) : (
          <aside
            style={{
              // Lebih lebar dari rel layar lain: basemap di puncaknya butuh
              // ruang mendatar agar sebaran sumur tidak terjepit jadi pita —
              // dan justru karena itu lebarnya tidak boleh ditetapkan di sini.
              // Berapa ruang yang pantas untuk basemap bergantung pada berapa
              // sumur yang sedang dikorelasikan, dan itu berubah tiap traverse.
              width: railWidth,
              flex: "none",
              background: "var(--rail)",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            {/* Kepala rel, yang tidak ikut bergulir: tombol lipat yang hanyut ke
                atas bersama basemap adalah tombol yang harus dicari dulu
                sebelum bisa ditekan. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 7px 4px 13px",
                borderBottom: "1px solid var(--line-soft)",
                flex: "none",
              }}
            >
              <span className="label">Correlation</span>
              <RailCollapseButton
                side="right"
                collapsed={false}
                label="Hide the correlation panel"
                onClick={() => setRailCollapsed(true)}
              />
            </div>
            <div
              className="scroll-y"
              style={{
                flex: 1,
                padding: "14px 13px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <Panel
                title="Basemap"
                meta={`${visibleWells.length}/${allWells.length}`}
              >
                <Dropdown
                  value={basemapSurface}
                  label="Surface"
                  options={[
                    { value: "", label: section?.datum_marker ? `Datum — ${section.datum_marker}` : "Datum" },
                    ...(panel.data?.marker_names ?? []).map((name) => ({ value: name, label: name })),
                  ]}
                  onChange={setBasemapSurface}
                  width="100%"
                  compact
                />
                <Basemap
                  unitSystem={project.unit_system}
                  wells={basemapWells}
                  extent={map.data?.extent ?? null}
                  // Isi peta adalah sumur yang tercentang di daftar "Wells
                  // correlated" di bawahnya; yang tidak tercentang tetap tergambar
                  // samar sebagai konteks.
                  activeIds={basemapActiveIds}
                  // Rel ini bergulir, dan peta setinggi 250 px yang duduk di panel
                  // ketiga berakhir di bawah lipatan pada layar laptop — panel yang
                  // tampak kosong, bukan peta. Ia naik ke puncak rel dan tumbuh
                  // secukupnya agar bentuk lapangan terbaca tanpa menggulir.
                  height={400}
                  order={basemapOrder}
                  markerName={basemapMarker}
                  // Huruf ujung peta dibaca dari nama lintasannya: memilih B–B′
                  // dan mendapat peta bertanda A–A′ adalah keterangan yang salah.
                  traverseName={section?.name ?? null}
                  layers={basemapLayers}
                  selectedName={well?.name ?? null}
                  onPick={(picked) => {
                    // Memilih sumur dari basemap: klik kepala sumur dan kolomnya
                    // muncul atau hilang. Urutan panel tetap mengikuti lintasan,
                    // not click order — a correlation sheet ordered by whatever the
                    // hand felt like stops reading as a section.
                    const target = allWells.find((w) => w.id === picked.id);
                    if (!target) {
                      toast(`${picked.name} is not part of this traverse.`);
                      return;
                    }
                    setHidden((prev) => {
                      const next = new Set(prev);
                      if (next.has(target.id)) next.delete(target.id);
                      // Satu panel harus tersisa, sama seperti daftar centangnya.
                      else if (total - next.size > 1) next.add(target.id);
                      return next;
                    });
                  }}
                />
                <BasemapLayerToggles layers={basemapLayers} onChange={setBasemapLayers} />
                <BasemapLegend />
                {map.loading && (
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)" }}>
                    Loading well positions…
                  </span>
                )}
                {map.error && <Notice tone="error">{map.error}</Notice>}
              </Panel>

              {/* Jaring radius, tepat di atas daftar sumurnya: saring dulu,
                  baru pilih. Ia menandai daftar itu dan tidak menyembunyikan
                  apa pun sendiri — satu putaran radius bisa mengembalikan sumur
                  yang barusan keluar, dan lembar korelasi yang diam-diam
                  kehilangan sumurnya terlihat persis seperti lembar yang memang
                  cuma berisi sekian sumur. */}
              <Panel title="Radius filter">
                <Checkbox
                  checked={centerWell !== null}
                  label="Filter wells by radius"
                  onChange={(on) =>
                    setCenterWell(on ? (centerWell ?? allWells[0]?.id ?? null) : null)
                  }
                />
                {centerWell !== null && (
                  <>
                    <Dropdown
                      label="PUSAT"
                      value={centerWell}
                      options={allWells.map((w) => ({ value: w.id, label: w.name }))}
                      onChange={setCenterWell}
                    />
                    <NumberField
                      label="Radius"
                      unit="km"
                      value={radiusKm}
                      min={0.01}
                      step={0.5}
                      onChange={setRadiusKm}
                    />
                    <Segmented
                      value={radiusMode}
                      size="sm"
                      options={[
                        { value: "surface", label: "Well head" },
                        { value: "trajectory", label: "Along the track" },
                      ]}
                      onChange={(next) => setRadiusMode(next as "surface" | "trajectory")}
                    />
                    <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                      This filter picks wells; it does not cut depth. Every well
                      shown still hangs at its whole depth. The bands say where a
                      neighbour comes close to the reference.
                    </span>
                    {screening.error && <Notice tone="error">{screening.error}</Notice>}
                    {screening.data?.note && <Notice tone="warn">{screening.data.note}</Notice>}
                    <Button
                      full
                      disabled={!terjaring.length}
                      onClick={() => {
                        // Aturan yang sudah berlaku di panel ini tetap berlaku:
                        // sekurangnya satu sumur harus tersisa, sebab korelasi
                        // tanpa sumur bukan keadaan kosong yang menjelaskan
                        // dirinya melainkan layar putih.
                        const lolos = new Set(terjaring);
                        const sisa = allWells.filter((w) => lolos.has(w.id));
                        if (!sisa.length) return;
                        setHidden(new Set(
                          allWells.filter((w) => !lolos.has(w.id)).map((w) => w.id)));
                      }}
                    >
                      {terjaring.length
                        ? `Keep the ${terjaring.length} that were caught`
                        : "Nothing was caught"}
                    </Button>
                  </>
                )}
              </Panel>

              {screening.data && screening.data.wells.length > 0 && (
                <Panel
                  title={`Caught — ${screening.data.kept} of ${screening.data.asked} wells`}
                  meta={`${radiusKm} km from ${screening.data.center_name}`}
                >
                  <ScreeningTable screening={screening.data} />
                </Panel>
              )}

              <Panel
                title="Wells correlated"
                meta={`${visibleWells.length}/${allWells.length}`}
              >
                <Toggle
                  label="Leave out wells without GR or RT"
                  checked={requireGrRt}
                  onChange={setRequireGrRt}
                />
                {lacking.size > 0 && (
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
                    {lacking.size} {lacking.size === 1 ? "well is" : "wells are"} left out.
                    RT may be read from ILD, LLD, RILD, RESD or another deep
                    resistivity; the header of each column names the one it used.
                  </span>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  <Button variant="ghost" onClick={() => setHidden(new Set())}>All</Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // Yang disisakan sumur pertama yang memang boleh tergambar;
                      // menyisakan sumur yang disingkirkan aturan berarti lembar kosong.
                      const keep = allWells.find((w) => !lacking.has(w.id)) ?? allWells[0];
                      setHidden(new Set(allWells.filter((w) => w !== keep).map((w) => w.id)));
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="scroll-y"
                  style={{ maxHeight: 190, display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {allWells.map((w) => (
                    <Checkbox
                      key={w.id}
                      label={w.name}
                      // Keadaan jaring berdiri sebagai keterangan di baris
                      // sumurnya sendiri. Sumur di luar jaring tetap berdiri di
                      // daftar dan tetap bisa dicentang tangan: yang menyaring
                      // adalah tandanya, bukan hilangnya baris itu.
                      meta={
                        lacking.has(w.id)
                          ? `no ${lacking.get(w.id)!.join(", ")}`
                          : keadaan.get(w.id)?.state
                      }
                      disabled={lacking.has(w.id)}
                      checked={!hidden.has(w.id) && !lacking.has(w.id)}
                      onChange={(on) => {
                        // A functional update, not a copy of `hidden`: several
                        // beberapa centang dalam satu putaran render membaca state
                        // the same state, and only the last click would survive.
                        setHidden((prev) => {
                          const next = new Set(prev);
                          if (on) next.delete(w.id);
                          // One panel must remain: a correlation with no wells is
                          // not an informative empty state, just a blank screen.
                          else if (
                            allWells.filter((x) => !next.has(x.id) && !lacking.has(x.id)).length > 1
                          ) next.add(w.id);
                          return next;
                        });
                      }}
                    />
                  ))}
                </div>
              </Panel>

              <Panel title="Colours" meta={`${curves.length} curves`}>
                <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)" }}>
                  CURVES
                </span>
                {curves.map((name) => (
                  <ColorPicker
                    key={name}
                    label={name}
                    value={resolveCorrColor(name, curveCfg[name])}
                    onChange={(colour) => editCurve(name, { color: colour })}
                  />
                ))}
                <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)", marginTop: 4 }}>
                  WELL PANELS
                </span>
                <div
                  className="scroll-y"
                  style={{ maxHeight: 170, display: "flex", flexDirection: "column", gap: 5 }}
                >
                  {visibleWells.map((w, index) => (
                    <ColorPicker
                      key={w.id}
                      label={w.name}
                      disabled={!mayWrite}
                      value={w.display_color || WELL_COLORS[index % WELL_COLORS.length]}
                      onChange={(colour) => void setWellColor(w.id, colour)}
                    />
                  ))}
                </div>
                <span style={{ font: "400 9px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
                  {mayWrite
                    ? "Click a legend row in the track header to set its line style, width, scale and shading as well. Well colours live on the server and are the same for every member of the project; curve settings stay in this browser."
                    : "Click a legend row in the track header to set up the curve. Your role is read-only; well colours cannot be changed."}
                </span>
                {/* Palet dikelola dari sini dan bukan dari kepala track, sebab
                    yang dibuat di sini berlaku untuk seluruh aplikasi — bukan
                    untuk track yang kebetulan sedang terbuka. Yang dipilih tetap
                    di kepala track, tempat kurvanya sedang dipandang. */}
                <Button variant="ghost" onClick={() => setPaletteOpen(true)}>
                  {Icon.plus({ size: 12 })} Manage colour palettes
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCurveCfg({});
                    if (mayWrite) {
                      visibleWells
                        .filter((w) => w.display_color)
                        .forEach((w) => void setWellColor(w.id, ""));
                    }
                  }}
                >
                  Reset to default colours
                </Button>
              </Panel>


              <Panel title="Markers" meta={`${tieLines.length}`}>
                {tieLines.length === 0 && (
                  <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-5)" }}>
                    No markers on the wells of this traverse yet.
                  </span>
                )}
                {tieLines.map((line) => (
                  // Baris ini juga sebuah pegangan: menekannya lalu melepasnya di
                  // sebuah kolom memasang marker bernama itu di sumur tersebut.
                  // Dari sini yang dipegang hanya namanya, belum sumur asalnya,
                  // sehingga sebuah nama bisa dibawa ke sumur yang jauh dari
                  // sumur mana pun yang sudah memilikinya.
                  <div
                    key={line.name}
                    onMouseDown={() => {
                      if (mayPickDepth) dragging.current = { marker: line.name, wellId: null };
                    }}
                    title={
                      mayPickDepth
                        ? `${line.name} — drag onto a column to set it on that well.`
                        : mayWrite
                          ? `${line.name} — switch to MD to set it by dragging.`
                          : line.name
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: mayPickDepth ? "grab" : "default",
                    }}
                  >
                    <span style={{ width: 16, height: 0, borderTop: `2px dashed ${line.color}`, flex: "none" }} />
                    <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-2)" }}>{line.name}</span>
                    <span style={{ flex: 1 }} />
                    {line.name === section?.datum_marker ? (
                      <span style={{ font: "400 9px var(--font-mono)", color: "var(--accent)" }}>datum</span>
                    ) : (
                      <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
                        {line.wells} wells
                      </span>
                    )}
                  </div>
                ))}
              </Panel>

              <Panel title="Marker depths" meta={section?.datum_marker}>
                {visibleWells.map((panelWell) => {
                  const marker = panelWell.markers.find((m) => m.name === section?.datum_marker);
                  return (
                    <div
                      key={panelWell.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "4px 7px",
                        background: "var(--panel-inset)",
                        border: "1px solid var(--line-strong)",
                        borderRadius: 5,
                      }}
                    >
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: marker?.confidence_color ?? "var(--ink-7)",
                          flex: "none",
                        }}
                      />
                      <span
                        style={{
                          font: "400 10px var(--font-sans)",
                          color: "var(--ink-2)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth: 0,
                        }}
                      >
                        {panelWell.name}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span
                        style={{
                          font: "500 10px var(--font-mono)",
                          color: "var(--ink)",
                          whiteSpace: "nowrap",
                          flex: "none",
                        }}
                      >
                        {marker ? `${marker.md.toFixed(1)} m` : "—"}
                      </span>
                      <span
                        style={{
                          font: "400 9px var(--font-mono)",
                          color: "var(--ink-6)",
                          width: 52,
                          textAlign: "right",
                        }}
                      >
                        {marker?.source ?? "—"}
                      </span>
                    </div>
                  );
                })}
              </Panel>

              {/* Panel ini menjawab pertanyaan yang tidak bisa dijawab gambar:
                  berapa. Lembar korelasi sudah menaruh kurva kelima sumur
                  berdampingan, tetapi "mana yang paling bagus porositasnya" hanya
                  bisa ditaksir dari lebar arsiran — dan taksiran itu berubah
                  bersama lebar panel. */}
              <Panel title="Well against well" meta={compareZone || "the whole interval"}>
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Dropdown
                      value={compareCurve}
                      options={curves.map((name) => ({ value: name, label: name }))}
                      onChange={setCompareCurve}
                      width="100%"
                      compact
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Dropdown
                      value={compareZone}
                      options={[
                        { value: "", label: "The whole interval" },
                        ...zoneNames.map((name) => ({ value: name, label: name })),
                      ]}
                      onChange={setCompareZone}
                      width="100%"
                      compact
                    />
                  </div>
                </div>
                {!curves.includes(compareCurve) && (
                  <Notice tone="warn">
                    {compareCurve} is not on display, so its samples were not asked for.
                    Switch it on in the Display panel.
                  </Notice>
                )}
                {comparison.map((row) => {
                  // Batang dibandingkan terhadap nilai terbesar di panel ini, bukan
                  // terhadap rentang tampilan kurvanya: yang ditanyakan di sini
                  // adalah "dibanding tetangganya", dan itulah yang harus diukur.
                  const best = Math.max(...comparison.map((r) => r.mean ?? 0), 0);
                  const share = row.mean !== null && best > 0 ? (row.mean / best) * 100 : 0;
                  return (
                    <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 7,
                                               padding: "4px 7px", background: "var(--panel-inset)",
                                               border: "1px solid var(--line-strong)", borderRadius: 5 }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%",
                                     background: row.color, flex: "none" }} />
                      <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-2)",
                                     whiteSpace: "nowrap", overflow: "hidden",
                                     textOverflow: "ellipsis", minWidth: 0, flex: 1 }}>
                        {row.name}
                      </span>
                      {row.mean === null ? (
                        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)",
                                       whiteSpace: "nowrap" }}>
                          {row.undrilled
                            ? "planned well"
                            : row.missingCurve
                            ? `no ${compareCurve}`
                            : row.beyondLog
                              ? "outside the logged interval"
                              : "zone absent"}
                        </span>
                      ) : (
                        <>
                          <span style={{ width: 46, height: 5, background: "var(--control-flat)",
                                         borderRadius: 3, overflow: "hidden", flex: "none" }}>
                            <span style={{ display: "block", width: `${share}%`, height: "100%",
                                           background: row.color }} />
                          </span>
                          <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink)",
                                         width: 46, textAlign: "right", flex: "none" }}>
                            {row.mean.toFixed(row.mean >= 10 ? 1 : 3)}
                          </span>
                          {/* Net di atas, gross di bawahnya: yang dicari orang di
                              kolom ini adalah berapa meter yang berguna, dan tebal
                              kotor hanyalah pembaginya. */}
                          <span style={{ font: "400 9px var(--font-mono)", width: 60,
                                         textAlign: "right", flex: "none", lineHeight: 1.35 }}>
                            {row.net !== null ? (
                              <>
                                <span style={{ color: "var(--ink-3)" }}>{row.net.toFixed(1)} m</span>
                                <br />
                                <span style={{ color: row.clipped ? "var(--amber)" : "var(--ink-7)" }}>
                                  /{row.thickness?.toFixed(0)}
                                  {row.clipped ? "*" : ""} · {(row.ntg ?? 0).toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <span style={{ color: "var(--ink-6)" }}>
                                {row.thickness !== null ? `${row.thickness.toFixed(0)} m` : `${row.n}×`}
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  {netWeighted ? (
                    <>
                      Mean {compareCurve}
                      {comparison[0]?.unit ? ` (${comparison[0].unit})` : ""} <b>above cutoff</b>,
                      net-pay weighted, with net reservoir thickness · gross thickness · N/G.
                      Read from the latest run of each well — computed at full resolution
                      with the cutoffs of the project parameter set, so the numbers are the
                      ones the CPI sheet uses.
                    </>
                  ) : (
                    <>
                      Raw mean {compareCurve}
                      {comparison[0]?.unit ? ` (${comparison[0].unit})` : ""} over{" "}
                      {compareZone
                        ? "the zone of that name in each well"
                        : "the whole interval the panel loaded"}
                      , from the decimated panel samples. The engine stores an above-cutoff
                      mean for PHIE, SW, VSH and PERM only — pick one of those together with
                      a zone to get its net number.
                    </>
                  )}{" "}
                  The bars are relative to the highest well in this panel.
                  {comparison.some((row) => row.clipped) && (
                    <>
                      {" "}
                      <b>*</b> the zone reaches outside the computed interval — the log does
                      not cover all of it, so its net is measured over the covered thickness
                      rather than over{" "}
                      {comparison
                        .filter((row) => row.clipped)
                        .map((row) => `${row.nominal?.toFixed(0)} m in ${row.name}`)
                        .join(", ")}
                      .
                    </>
                  )}
                </span>
              </Panel>

              <Panel title="Display">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {AVAILABLE_CURVES.map((name) => (
                    <Chip
                      key={name}
                      active={curves.includes(name)}
                      onClick={() =>
                        setTrackGroups(
                          curves.includes(name)
                            ? trackGroups.map((g) => g.filter((c) => c !== name))
                            : [...trackGroups, [name]],
                        )
                      }
                    >
                      {name}
                    </Chip>
                  ))}
                </div>

                <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)", marginTop: 4 }}>
                  TRACK LAYOUT
                </span>
                {/* Satu baris per kotak. Kurva kedua dan seterusnya bisa
                    dikeluarkan menjadi kotak sendiri; kurva pertama sebuah kotak
                    bisa digabungkan ke kotak sebelah kirinya. Yang pertama di
                    seluruh lembar tidak punya kiri, jadi tombolnya mati. */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {trackGroups.map((names, index) => (
                    <div
                      key={names.join("+")}
                      style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}
                    >
                      <span
                        style={{
                          font: "400 9px var(--font-mono)",
                          color: "var(--ink-6)",
                          width: 14,
                          flex: "none",
                        }}
                      >
                        {index + 1}
                      </span>
                      {names.map((name, posisi) => (
                        // `Chip` tidak menerima `title`, dan menambahkannya di
                        // sana berarti menyentuh komponen yang dipakai setiap
                        // layar demi satu keterangan di satu tempat.
                        <span
                          key={name}
                          title={
                            posisi === 0
                              ? index === 0
                                ? "The first box — there is no box to its left"
                                : `Merge ${name} into box ${index}`
                              : `Take ${name} out into a box of its own`
                          }
                        >
                        <Chip
                          active
                          onClick={() =>
                            setTrackGroups(
                              posisi === 0
                                ? index === 0
                                  ? trackGroups
                                  : mergeIntoTrack(
                                      trackGroups,
                                      name,
                                      trackGroups[index - 1][trackGroups[index - 1].length - 1],
                                    )
                                : splitFromTrack(trackGroups, name),
                            )
                          }
                        >
                          {posisi === 0 ? (index === 0 ? "" : "← ") : "⤓ "}
                          {name}
                        </Chip>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
                <span style={{ font: "400 9.5px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
                  Several curves may share one box — GR and VSH, or RHOB and NPHI.
                  Each keeps its own scale; the numbers printed in the header belong
                  to the first curve of the box, because one box holds only one row
                  of numbers.
                </span>
                {/* Jalan pulang. Sejak susunan lembar pindah ke server, ia tidak
                    lagi bisa dikembalikan dengan membersihkan penyimpanan
                    peramban — dan sebuah lembar yang terlanjur digabungkan
                    seluruhnya ke satu kotak, atau yang kurvanya dimatikan semua,
                    tidak punya jalan kembali sama sekali. Tanpa dialog
                    konfirmasi: yang hilang adalah susunan yang bisa disusun
                    ulang dalam beberapa klik, dan konfirmasi untuk kerugian
                    sekecil itu hanya melatih orang menekan "ya". */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTrackGroups(CORR_DEFAULT_TRACKS);
                    setGap(CORR_GAP_DEFAULT);
                  }}
                  title={
                    mayWrite
                      ? "Puts the box layout and gap widths back to the defaults, for every member of the project"
                      : "Puts the box layout and gap widths back to the defaults, for your own display"
                  }
                >
                  Restore the default layout
                </Button>

                <Toggle label="Lithology strip" checked={showLitho} onChange={setShowLitho} />
                <Toggle label="Curve shading" checked={shading} onChange={setShading} />
                <Toggle label="Latar putih" checked={paper} onChange={setPaper} />
                <Toggle label="Flatten on datum" checked={flatten} onChange={setFlatten} />
                <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)", marginTop: 4 }}>
                  DEPTH REFERENCE
                </span>
                <Segmented
                  value={depthMode}
                  onChange={(value) => setDepthMode(value as DepthReference)}
                  options={DEPTH_ORDER.map((value) => ({ value, label: DEPTH_LABEL[value] }))}
                />
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  {DEPTH_CAPTION[depthMode]}
                </span>
                {depthMode !== "md" && assumedVertical.length > 0 && (
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--warn-ink)", lineHeight: 1.6 }}>
                    No surveyed trajectory, assumed vertical: {assumedVertical.join(", ")}.
                  </span>
                )}
                <Slider
                  label="Draw a marker present in"
                  value={minTieWells}
                  min={2}
                  max={Math.max(2, visibleWells.length)}
                  step={1}
                  onChange={(value) => setMinTieWells(Math.round(value))}
                  format={(value) => `≥ ${Math.round(value)} wells`}
                />
              </Panel>

              <Panel
                title="Wells per panel"
                meta={perPanel === 1 ? "apart" : `${perPanel} stacked`}
              >
                <Segmented
                  value={String(perPanel)}
                  onChange={(value) => setPerPanel(Number(value))}
                  options={[
                    { value: "1", label: "1" },
                    { value: "2", label: "2" },
                    { value: "3", label: "3" },
                    { value: "4", label: "4" },
                  ]}
                />
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
                  Stacked wells are drawn in their own depth space. When the panel
                  is flattened on a datum, the curves line up on the marker — not on
                  measured depth.
                </span>
              </Panel>

              {hidden.size > 0 && (
                <Panel title="Wells hidden">
                  {[...hidden].map((id) => {
                    const entry = panel.data?.wells.find((w) => w.id === id);
                    return (
                      <Button
                        key={id}
                        variant="ghost"
                        full
                        onClick={() => {
                          const next = new Set(hidden);
                          next.delete(id);
                          setHidden(next);
                        }}
                      >
                        {Icon.eye({ size: 12 })} {entry?.name}
                      </Button>
                    );
                  })}
                </Panel>
              )}

              {panel.data?.missing.length ? (
                <Notice>
                  Curves incomplete in: {panel.data.missing.join(", ")}. Run the computation for
                  those wells.
                </Notice>
              ) : null}

              <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                <Button variant="ghost" full onClick={() => window.print()}>
                  Export section (PDF)
                </Button>
              </div>
            </div>
          </aside>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          height: 36,
          flex: "none",
          padding: "0 12px",
          background: "var(--menubar)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <FooterScale
          scale={sheet.scale}
          fitted={sheet.fitted}
          onStep={(direction) => setScaleMode(stepScale(sheet.scale, direction))}
          onPick={(mode) => setScaleMode(mode)}
        />
        <FooterControl
          label="Well spacing"
          value={CORR_GAPS.find((g) => g.value === gap)?.label ?? `${Math.round(gap * 100)}%`}
        />
        <FooterControl label="Cursor" value={cursorDepth !== null ? `${cursorDepth.toFixed(1)} m` : "—"} />
        <span style={{ flex: 1 }} />
        {/* Kendali yang tak terlihat sama saja dengan tidak ada — kalimat yang
            sama berlaku di sini seperti di kaki lembar log, dan bunyinya harus
            sama pula: dua layar yang menerangkan gerakan yang sama dengan dua
            kalimat berbeda membuat pembacanya mengira gerakannya berbeda. */}
        <span
          style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)", whiteSpace: "nowrap" }}
        >
          wheel: zoom · drag the ruler: stretch · drag the sheet: pick an interval
        </span>
        <span style={{ color: "var(--ink-7)" }}>·</span>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {panel.data?.marker_names.length ?? 0} markers · {visibleWells.length} wells correlated
          {panel.data?.missing.length
            ? ` · ${panel.data.missing.length} need review`
            : ""}
        </span>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={
            menu.marker
              ? `${menu.marker} · ${panel.data?.wells.find((w) => w.id === menu.wellId)?.name ?? ""}`
              : `${(menu.md ?? 0).toFixed(1)} m · ${panel.data?.wells.find((w) => w.id === menu.wellId)?.name ?? ""}`
          }
          onClose={() => setMenu(null)}
          // Yang menyentuh kedalaman hanya terbuka pada MD; yang tidak —
          // menamai, mewarnai, menjadikan datum, menghapus — tetap terbuka
          // pada acuan mana pun. Sebuah menu yang lenyap seluruhnya di TVDSS
          // akan terbaca sebagai marker yang tak bisa diapa-apakan lagi.
          items={
            !mayWrite
              ? [{ label: "Your role is read-only.", onClick: undefined }]
              : !menu.marker
                ? mayPickDepth
                  ? [
                      {
                        label: "Set a marker here…",
                        icon: Icon.markerAdd({ size: 11 }),
                        onClick: () =>
                          setMarkerDraft({
                            md: menu.md ?? 0,
                            wellId: menu.wellId,
                            wellName:
                              panel.data?.wells.find((w) => w.id === menu.wellId)?.name ?? "",
                          }),
                      },
                    ]
                  : [{ label: `Switch to MD to set a marker — ${DEPTH_LABEL[depthMode]} is a derived axis.`,
                       onClick: undefined }]
                : [
                    ...(mayPickDepth
                      ? [
                          {
                            label: "Move to the cursor",
                            icon: Icon.markerAdd({ size: 11 }),
                            onClick: () => {
                              if (cursorDepth !== null) void moveMarker(menu.wellId, menu.marker, cursorDepth);
                            },
                          },
                        ]
                      : []),
                    { label: "Rename and recolour…", onClick: () => setRenaming({ wellId: menu.wellId, marker: menu.marker }) },
                    { label: "Make it the datum", onClick: () => void setDatum(menu.marker) },
                    ...(mayPickDepth
                      ? [
                          {
                            label: "Apply to all wells",
                            onClick: () => {
                              const source = panel.data?.wells
                                .find((w) => w.id === menu.wellId)
                                ?.markers.find((m) => m.name === menu.marker);
                              if (source) void applyToAll(menu.marker, source.md);
                            },
                          },
                        ]
                      : []),
                    { separator: true, label: "" },
                    {
                      label: "Delete this marker in this well",
                      danger: true,
                      onClick: () => void deleteMarker(menu.wellId, menu.marker),
                    },
                  ]
          }
        />
      )}

      {markerDraft && (
        <MarkerDialog
          projectId={project.id}
          wellId={markerDraft.wellId}
          wellName={markerDraft.wellName}
          draft={markerDraft}
          onClose={() => setMarkerDraft(null)}
          onSaved={(message) => {
            toast(message);
            setMarkerDraft(null);
            panel.reload();
          }}
        />
      )}

      {renaming && panel.data && (
        <RenameMarkerDialog
          panelWell={panel.data.wells.find((w) => w.id === renaming.wellId)!}
          markerName={renaming.marker}
          onClose={() => setRenaming(null)}
          onSaved={() => {
            setRenaming(null);
            panel.reload();
          }}
        />
      )}

      {paletteOpen && (
        <PaletteEditor onClose={() => setPaletteOpen(false)} persist={simpanPalet} />
      )}
    </div>
  );
}

/**
 * Skala tegak, dan tombol yang benar-benar mengubahnya.
 *
 * Sebelumnya di sini hanya tertulis "1 : 200" — angka dari basis data yang
 * tidak pernah menentukan apa pun di layar. Sekarang ia adalah kendali: dua
 * tombol melangkah seperlima, daftarnya menyimpan skala baku, dan "Pas"
 * mengembalikan seluruh jendela ke dalam satu layar.
 */
/**
 * Titik terluar kiri dan kanan garis ikat yang masih di dalam lembar (0–1000).
 * Null bila tak satu pun terlihat.
 */
function visibleEnds(points: [number, number][]): readonly [[number, number], [number, number]] | null {
  const inside = points.filter(([, y]) => y >= 0 && y <= 1000);
  return inside.length ? [inside[0], inside[inside.length - 1]] : null;
}

/**
 * Ruang kosong yang memisahkan dua panel sumur.
 *
 * Bukan `gap` milik flexbox, dan itu disengaja. `gap` CSS dinyatakan dalam
 * piksel, sedangkan garis ikat lembar ini digambar di ruang abstrak yang
 * direntangkan mengikuti lebar panel; spasi berupa piksel membuat lebar kolom
 * tidak lagi bisa dihitung tanpa mengukur panelnya lebih dulu, dan yang diukur
 * akan basi satu bingkai setiap kali rel kanan dilipat. Sebagai elemen
 * ber-`flex` sendiri, spasi ini tumbuh dan menyusut bersama kolomnya, dan
 * `columnBounds` di `lib/corrspacing` menghitung tempat yang persis sama.
 *
 * Kepalanya setinggi kepala panel supaya lembarnya mulai pada garis yang sama
 * di seberang spasi — kepala yang lebih pendek beberapa piksel membuat kedua
 * sumur tampak tergantung pada kedalaman yang berbeda.
 */
function PanelGap({
  flex,
  headerH,
  label,
}: {
  flex: number;
  headerH: number;
  /** Jarak permukaan ke panel berikutnya. Kosong bila tidak diketahui. */
  label: string | null;
}) {
  return (
    // Tanpa `aria-hidden`: sejak spasi menyala, jaraknya hanya tertulis di
    // sini — chip di kepala sumur sengaja dimatikan supaya angkanya tidak
    // tercetak dua kali. Menyembunyikan kotak ini berarti membuang satu-satunya
    // tempat angka itu ada bagi yang membacanya lewat pembaca layar.
    <div
      style={{
        flex,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: "var(--canvas)",
      }}
    >
      <div
        style={{
          height: headerH,
          flex: "none",
          position: "sticky",
          top: 0,
          zIndex: 6,
          background: "var(--titlebar)",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {label && (
          <span
            title="Surface distance between the two wells"
            aria-label={`Surface distance between the two wells: ${label}`}
            style={{
              font: "500 9px var(--font-mono)",
              color: "var(--ink-5)",
              whiteSpace: "nowrap",
              // Spasi sempit tidak cukup untuk sebuah angka; yang tidak muat
              // dipotong diam-diam alih-alih melebarkan kepala dan menggeser
              // seluruh lembar di sebelahnya.
              textOverflow: "clip",
            }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

function FooterScale({
  scale,
  fitted,
  onStep,
  onPick,
}: {
  scale: number;
  fitted: boolean;
  onStep: (direction: 1 | -1) => void;
  onPick: (mode: ScaleMode) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <span style={{ font: "500 9.5px var(--font-mono)", color: "var(--ink-6)", letterSpacing: ".06em" }}>
        Vertical scale
      </span>
      <Button variant="ghost" title="Stretch vertically" onClick={() => onStep(1)}>
        +
      </Button>
      <Button variant="ghost" title="Compress vertically" onClick={() => onStep(-1)}>
        −
      </Button>
      <Dropdown
        value={fitted ? "fit" : String(scale)}
        options={[
          { value: "fit", label: "Fit" },
          ...[...new Set([...DEPTH_SCALE_PRESETS, ...(fitted ? [] : [scale])])]
            .sort((a, b) => a - b)
            .map((step) => ({ value: String(step), label: `1:${step}` })),
        ]}
        onChange={(value) => onPick(value === "fit" ? "fit" : Number(value))}
        width={84}
        compact
      />
    </div>
  );
}

function FooterControl({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <span style={{ font: "500 9.5px var(--font-mono)", color: "var(--ink-6)", letterSpacing: ".06em" }}>
        {label}
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          height: 24,
          padding: "0 9px",
          background: "var(--control)",
          border: "1px solid var(--border-control)",
          borderRadius: "var(--radius)",
          font: "400 10.5px var(--font-mono)",
          color: "var(--ink)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function WellColumn({
  wells,
  swatch,
  wellColor,
  curveCfg,
  distanceToNext,
  units,
  groups,
  window: depthWindow,
  flatten,
  showLitho,
  shading,
  active,
  markerFraction,
  headRef,
  onHide,
  onMarkerMenu,
  onMarkerDragStart,
  onColumnMenu,
  onDepthHover,
  onDrop,
  onCurveEdit,
  onCurveAuto,
  onSpectrumEdit,
  onSpectrumRemove,
  onScaleToggle,
  onReorder,
  onWellColor,
  panelDrag,
}: {
  wells: PanelWell[];
  swatch: string;
  wellColor: (well: PanelWell, index: number) => string;
  /** Setelan per mnemonic, dipakai bersama oleh setiap panel di lembar ini. */
  curveCfg: CorrCurveStore;
  units: ReturnType<typeof unitsFor>;
  distanceToNext: number | null;
  /** Susunan track panel ini: satu larik mnemonic per kotak. */
  groups: string[][];
  window: [number, number] | null;
  flatten: boolean;
  showLitho: boolean;
  shading: boolean;
  active: boolean;
  markerFraction: (panelWell: PanelWell, md: number) => number | null;
  /** Dipasang pada baris kepala track panel pertama saja; lihat pemanggilnya. */
  headRef?: (element: HTMLDivElement | null) => void;
  onHide: () => void;
  onMarkerMenu: (event: React.MouseEvent, markerName: string) => void;
  onMarkerDragStart: (markerName: string) => void;
  /**
   * Kursor di atas kolom: MD di sumur ini, dan kedudukannya pada sumbu
   * bersama panel. Keduanya, sebab kaki layar melaporkan yang pertama dan
   * mistar kedalaman mencetak yang kedua — dan diratakan pada datum keduanya
   * berselisih sejauh datum sumur itu.
   */
  onDepthHover: (depth: number | null, axis: number | null) => void;
  onDrop: (depth: number) => void;
  /** Klik kanan pada kolom di luar marker mana pun. */
  onColumnMenu: (event: React.MouseEvent, depth: number) => void;
  onCurveEdit: (name: string, patch: Partial<CurveConfig>) => void;
  onCurveAuto: (name: string) => void;
  onSpectrumEdit: (name: string, patch: Partial<SpectrumConfig>) => void;
  onSpectrumRemove: (name: string) => void;
  onScaleToggle: (name: string) => void;
  /**
   * Menyusun ulang kolom kurva dengan menyeret kepalanya.
   *
   * Satu susunan untuk seluruh lembar, bukan satu per sumur: kolom yang
   * berbeda urutannya dari satu panel ke panel berikutnya adalah lembar
   * korelasi yang tidak bisa dibaca mendatar — dan membaca mendatar adalah
   * satu-satunya alasan lembar ini ada.
   */
  onReorder: (name: string, targetName: string) => void;
  /** Kosong bila peran pengguna hanya-baca: warna sumur tersimpan di server. */
  onWellColor?: (wellId: number, colour: string) => void;
  /**
   * Seretan penyusun ulang panel, dari `useReorderDrag` di layarnya.
   *
   * Dua bagian: `boxProps` menandai panel ini sebagai kotak yang diukur, dan
   * `handleProps` dipasang pada kepalanya — yang ditekan bukan kotak yang
   * diukur, sebab menyeret lembarnya sendiri sudah berarti memilih interval,
   * dan menyeret rel kepala kurva di dalamnya menyusun ulang kurva.
   * Kosong bila peran pengguna hanya-baca: urutan ini milik lintasan, dan
   * lintasan itu milik bersama.
   */
  panelDrag?: {
    boxProps: Record<string, unknown>;
    handleProps: Record<string, unknown>;
    dragging: boolean;
    dropEdge: "left" | "right" | null;
  };
}) {
  // The first well owns the panel's markers, zones and lithology strip. The
  // rest are overlaid as curves only: three sets of marker lines on top of
  // each other would hide the very thing the panel exists to show.
  const panelWell = wells[0];
  const stacked = wells.length > 1;

  /**
   * Geseran kedalaman sebuah sumur ke ruang bersama panel.
   *
   * Setiap sumur punya ruang kedalamannya sendiri. Diratakan pada datum,
   * masing-masing digeser oleh datumnya sendiri sehingga kurva yang ditumpuk
   * berbaris pada markernya dan bukan pada kedalaman terukur — dan itulah
   * seluruh alasan menumpuknya.
   */
  const shiftOf = (w: PanelWell) => (flatten && w.datum_md !== null ? -w.datum_md : 0);

  // Kepala kolom di sini adalah kepala track lembar log, dan seretnya pun
  // seretan yang sama — `tracks[i].id` adalah mnemonic kurvanya, jadi yang
  // disusun ulang adalah daftar kurva lembar ini.
  // Yang disusun ulang adalah track, bukan mnemonic: sebuah kotak yang memuat
  // GR dan VSH berpindah sebagai satu kotak, sebab yang menumpuk keduanya
  // memang menumpuknya untuk dibaca bersama.
  const reorder = useReorderDrag((from, to) => {
    const moved = groups[from];
    const target = groups[to];
    if (moved && target) onReorder(moved[0], target[0]);
  });

  const tracks = useMemo<TrackSpec[]>(
    () =>
      groups.map((names) =>
        corrGroupSpec({
          names,
          wells,
          configs: curveCfg,
          shift: shiftOf,
          wellColor,
          stacked,
          shading,
        }),
      ),
    [groups, wells, curveCfg, flatten, stacked, shading],
  );

  /**
   * Zona panel ini, digeser ke ruang bersama.
   *
   * Sumur tanpa datum tidak punya kedudukan sama sekali di lembar yang
   * diratakan — sebagaimana markernya — jadi zonanya tidak digambar di
   * kedalaman terukurnya seolah-olah ia terletak di sana.
   */
  const zones = useMemo(() => {
    if (!panelWell || (flatten && panelWell.datum_md === null)) return [];
    const offset = shiftOf(panelWell);
    return panelWell.zones.map((zone) => ({
      ...zone,
      top: zone.top + offset,
      base: zone.base + offset,
    }));
  }, [panelWell, flatten]);

  if (!depthWindow || !wells.length) return null;

  /** Screen fraction back to MD for this well. */
  const toDepth = (fraction: number) => {
    const value = depthWindow[0] + fraction * (depthWindow[1] - depthWindow[0]);
    return flatten && panelWell.datum_md !== null ? value + panelWell.datum_md : value;
  };

  /**
   * Suntingan dari kepala track, dipulangkan kepada yang memilikinya.
   *
   * Pada panel bertumpuk yang membedakan garis adalah sumurnya, jadi baris
   * legendanya bernama sumur dan warnanya warna sumur — yang hidup di server.
   * Sisanya — bentuk garis, ketebalan, skala, arsiran — tetap milik mnemonic,
   * sebab dua sumur yang digambar dengan skala berbeda tidak bisa dibandingkan.
   */
  const editFromHead = (_trackId: string, curveKey: string, patch: Partial<CurveConfig>) => {
    if (stacked && typeof patch.color === "string") {
      // Dicari lewat id, bukan lewat nama yang tercetak di legendanya: dua
      // sumur bernama sama dalam satu panel akan membuat pencarian nama
      // mewarnai sumur yang salah, dan tidak ada yang memberitahu.
      const target = wells.find((w) => String(w.id) === keyWellId(curveKey));
      if (target) {
        onWellColor?.(target.id, patch.color);
        return;
      }
    }
    // Mnemonic dibaca dari kunci kurvanya, bukan dari id tracknya. Sejak sebuah
    // track boleh memuat lebih dari satu mnemonic, id track adalah gabungan
    // keduanya — "GR+VSH" — dan menyetel lewat id berarti menulis setelan ke
    // mnemonic yang tidak pernah ada, sementara kurva yang diklik tidak
    // berubah sedikit pun.
    onCurveEdit(keyMnemonic(curveKey), patch);
  };

  return (
    <div
      {...(panelDrag?.boxProps ?? {})}
      style={{
        flex: 1,
        minWidth: 0,
        borderRight: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        outline: active ? "1px solid var(--accent)" : undefined,
        outlineOffset: -1,
        // Yang dibawa tangan dibuat pudar, dan tempat mendaratnya diberi garis
        // di sisi yang benar — sebagaimana kepala track di lembar log.
        opacity: panelDrag?.dragging ? 0.5 : 1,
        boxShadow: panelDrag?.dropEdge
          ? `inset ${panelDrag.dropEdge === "left" ? "" : "-"}3px 0 0 0 var(--accent)`
          : undefined,
      }}
    >
      <header
        data-corr-head
        {...(panelDrag?.handleProps ?? {})}
        style={{
          height: HEADER_H,
          flex: "none",
          // Tanpa ini, jari yang menyeret kepala panel menggulirkan lembarnya.
          touchAction: panelDrag ? "none" : undefined,
          padding: "5px 7px",
          background: "var(--titlebar)",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // Nama sumur harus tetap terlihat saat lembarnya digulir; panel
          // sedalam tiga layar yang kolomnya tak bernama tidak bisa dibaca.
          position: "sticky",
          top: 0,
          // Di atas pegangan marker, yang duduk di z-index 4. Pegangan itu
          // tersebar di sepanjang lembar, jadi menggulir sejauh apa pun akan
          // membawa salah satunya lewat di belakang kepala yang menempel — dan
          // sebuah nama sumur yang tercoret garis marker terbaca sebagai nama
          // yang dicoret, bukan sebagai marker yang lewat.
          zIndex: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {/* Grip ini sudah tergambar sejak dulu tanpa melakukan apa-apa; kini
              ia memang pegangannya. Kursornya berubah supaya itu terbaca
              sebelum dicoba. */}
          <span
            title={panelDrag ? "Drag to move this panel" : undefined}
            style={{ display: "flex", cursor: panelDrag ? "grab" : "default" }}
          >
            {Icon.grip({ size: 8, color: panelDrag ? "var(--ink-5)" : "var(--ink-7)" })}
          </span>
          <span
            style={{
              font: "600 10.5px var(--font-sans)",
              color: active ? "var(--accent)" : "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                marginRight: 5,
                background: swatch,
                border: "1px solid var(--line-strong)",
                verticalAlign: "middle",
              }}
            />
            {stacked ? `${wells.length} wells` : panelWell.name}
            {!stacked && (
              <span style={{ color: "var(--ink-6)", fontWeight: 400 }}> [SSTVD]</span>
            )}
          </span>
          <span style={{ flex: 1 }} />
          {distanceToNext !== null && (
            <span
              title="Surface distance to the next panel"
              style={{
                font: "500 9px var(--font-mono)",
                color: "var(--ink-5)",
                border: "1px solid var(--line)",
                borderRadius: 3,
                padding: "1px 4px",
                whiteSpace: "nowrap",
              }}
            >
              {`${units.formatDistance(distanceToNext)} →`}
            </span>
          )}
          <button
            type="button"
            title="Hide well"
            onClick={onHide}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-6)", padding: 0 }}
          >
            {Icon.eyeOff({ size: 12 })}
          </button>
        </div>
        {stacked ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0 7px" }}>
            {wells.map((w, i) => (
              <span
                key={w.id}
                style={{ display: "flex", alignItems: "center", gap: 3, font: "400 9px var(--font-mono)" }}
              >
                <span
                  style={{
                    width: 6,
                    height: 2,
                    background: wellColor(w, i),
                    flex: "none",
                  }}
                />
                <span style={{ color: w.datum_md === null && flatten ? "var(--ink-7)" : "var(--ink-4)" }}>
                  {w.name}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
            {/* Kepala panel menyebut rentang kedalaman sumurnya. Sumur rencana
                tidak punya rentang karena belum dibor, bukan karena lognya belum
                diimpor — sama seperti baris bandingnya di bawah. */}
            {panelWell.md_range
              ? `${panelWell.md_range[0].toFixed(0)}–${panelWell.md_range[1].toFixed(0)} m`
              : panelWell.status === "planned"
                ? "planned well"
                : "no curves"}
            {flatten && panelWell.datum_md === null ? " · no datum" : ""}
            {/* Kurva yang diperankan nama lain disebut: "RT ← RILD" adalah
                satu-satunya tempat yang mengatakan dari mana angka di track
                resistivitas kolom ini berasal. */}
            {Object.entries(panelWell.curves)
              .filter(([name, c]) => c.source && c.source !== name)
              .map(([name, c]) => ` · ${name} ← ${c.source}`)
              .join("")}
          </span>
        )}
      </header>

      {/* Kepala track — sama persis dengan kepala lembar log, dan itu bukan
          kebetulan. Baris legenda di sini adalah tombolnya sendiri: warna,
          bentuk garis, ketebalan, kedua ujung skala dan arsiran sebuah kurva
          disetel di tempat kurva itu sedang dipandang, bukan di dalam panel
          yang harus dicari lebih dulu di rel kanan. */}
      <div
        ref={headRef}
        data-corr-head
        style={{
          display: "flex",
          alignItems: "stretch",
          flex: "none",
          background: "var(--toolbar)",
          borderBottom: "1px solid var(--line)",
          // Menempel tepat di bawah nama sumur, dan seperti nama itu ia harus
          // tetap terbaca sepanjang lembar digulir: sebuah kurva tanpa skala
          // hanyalah sebuah bentuk — dan seperti nama itu pula ia harus berada
          // di atas pegangan marker yang lewat di belakangnya.
          position: "sticky",
          top: HEADER_H,
          zIndex: 6,
        }}
        {...reorder.rowProps}
      >
        {tracks.map((track, index) => (
          <TrackHeader
            key={track.id}
            track={track}
            dragProps={reorder.itemProps(index)}
            dragging={reorder.dragging === index}
            dropEdge={
              reorder.dragging === null || reorder.dropIndex !== index || reorder.dragging === index
                ? null
                : reorder.dropIndex < reorder.dragging
                  ? "left"
                  : "right"
            }
            // Dua pembagian, bukan lima. Kolom lembar korelasi selebar seratus
            // piksel, dan lima angka pada sumbu PHIE — 0,5 0,375 0,25 0,125 0 —
            // tercetak saling menindih: sebuah sumbu yang tak terbaca sama
            // sekali, ditukar dengan garis bantu yang tak seorang pun hitung.
            gridDivisions={2}
            weight={stacked ? 1 : 1.2}
            // Kolom terakhir menggantungkan kotak suntingnya ke kiri: sebuah
            // kotak selebar 176 piksel yang lahir di tepi kanan panel keluar
            // dari lembar, dan tidak ada yang menggulirnya kembali.
            align={index === tracks.length - 1 ? "right" : "left"}
            minWidth={0}
            onCurveEdit={editFromHead}
            // Kurva disebut lewat kuncinya, jadi mnemonicnya dibaca dari sana.
            onCurveAuto={(_trackId, curveKey) => onCurveAuto(keyMnemonic(curveKey))}
            // Pita warna dan sumbu adalah milik kotaknya, dan sebuah kotak
            // hanya punya satu dari masing-masing. Yang memegangnya adalah
            // mnemonic pertama track — yang sama yang memegang penggarisnya —
            // sebab dua pita di satu kotak berarti yang kedua menutupi yang
            // pertama, dan dua sumbu berarti salah satunya berbohong.
            onSpectrumEdit={(trackId, patch) => onSpectrumEdit(trackId.split("+")[0], patch)}
            onSpectrumRemove={(trackId) => onSpectrumRemove(trackId.split("+")[0])}
            onScaleToggle={(trackId) => onScaleToggle(trackId.split("+")[0])}
          />
        ))}
        {/* Sel kosong selebar strip litologi di bawahnya. Kepala yang meleset
            empat belas piksel dari tracknya adalah kepala yang berbohong
            tentang skala, dan itu terbaca sebagai kesalahan data. */}
        {showLitho && (
          <div style={{ width: 14, flex: "none", borderLeft: "1px solid var(--line-soft)" }} />
        )}
      </div>

      <div
        style={{ flex: 1, display: "flex", minHeight: 0, position: "relative" }}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const fraction = (event.clientY - rect.top) / rect.height;
          onDepthHover(
            toDepth(fraction),
            depthWindow[0] + fraction * (depthWindow[1] - depthWindow[0]),
          );
        }}
        onMouseLeave={() => onDepthHover(null, null)}
        onMouseUp={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          onDrop(toDepth((event.clientY - rect.top) / rect.height));
        }}
        // Marker yang sudah ada menghentikan peristiwanya sendiri, jadi yang
        // sampai ke sini hanyalah klik kanan pada kolom kosong — dan itu satu
        // permintaan yang jelas: pasang sesuatu di kedalaman ini.
        onContextMenu={(event) => {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          onColumnMenu(event, toDepth((event.clientY - rect.top) / rect.height));
        }}
      >
        {tracks.map((track) =>
          track.curves.length ? (
            <LogTrack
              key={track.id}
              track={track}
              top={depthWindow[0]}
              base={depthWindow[1]}
              zones={zones}
              depthGrid={26}
              // Sama dengan kepalanya. Garis kisi yang tidak jatuh di tempat
              // angkanya tercetak membuat pembaca yang menghitung sepanjang
              // kisi membaca angka yang salah.
              gridDivisions={2}
              weight={stacked ? 1 : 1.2}
              // Lembar korelasi tidak punya picker: bacaannya dibandingkan
              // antar sumur lewat panel Compare, dan sederet angka di atas
              // enam kolom sekaligus hanya menutupi kurvanya.
              showReadings={false}
              // Tanpa lantai lebar. Yang harus muat berdampingan di sini adalah
              // sumur-sumurnya, dan panel yang menolak menyempit membuat
              // traverse enam sumur tidak bisa dipandang sekaligus.
              minWidth={0}
            />
          ) : (
            // Kolomnya tetap ada meski sumur ini tidak membawa kurvanya: garis
            // ikat digambar sebagai satu overlay di atas seluruh lembar, dan
            // panel yang kehilangan satu kolom menggeser tetangganya sehingga
            // setiap garis menunjuk sumur yang salah.
            <div
              key={track.id}
              style={{
                position: "relative",
                flex: track.flex,
                minWidth: 0,
                borderRight: "1px solid var(--line)",
                background: "var(--canvas-deep)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <span
                style={{
                  font: "400 8.5px var(--font-mono)",
                  color: "var(--ink-7)",
                  transform: "rotate(-90deg)",
                  whiteSpace: "nowrap",
                }}
              >
                {track.title} —
              </span>
            </div>
          ),
        )}

        {showLitho && (
          <div style={{ position: "relative", width: 14, flex: "none", borderLeft: "1px solid var(--line-soft)" }}>
            {panelWell.zones.map((zone) => {
              const topFraction = markerFraction(panelWell, zone.top);
              const baseFraction = markerFraction(panelWell, zone.base);
              if (topFraction === null || baseFraction === null) return null;
              return (
                <div
                  key={zone.name}
                  title={zone.name}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: `${topFraction * 100}%`,
                    height: `${(baseFraction - topFraction) * 100}%`,
                    background: zone.color,
                    opacity: 0.55,
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Marker handles */}
        {panelWell.markers.map((marker) => {
          const fraction = markerFraction(panelWell, marker.md);
          if (fraction === null || fraction < 0 || fraction > 1) return null;
          return (
            <div
              key={marker.id}
              onMouseDown={() => onMarkerDragStart(marker.name)}
              // Ditahan supaya seretan ini tidak sampai ke lembar di
              // belakangnya, yang akan menafsirkannya sebagai zoom kotak —
              // dan satu seretan tidak boleh memindahkan sebuah top sekaligus
              // melempar pandangan ke interval lain.
              onPointerDown={(event) => event.stopPropagation()}
              onContextMenu={(event) => {
                event.preventDefault();
                // Ditahan di sini supaya klik kanan ini tidak ikut sampai ke
                // kolom di belakangnya. Kolom itu menafsirkan setiap klik
                // kanan sebagai "pasang marker di kedalaman ini", dan karena
                // ia menerima peristiwa yang sama belakangan, menu miliknyalah
                // yang menang — marker yang sudah ada jadi tak bisa disunting,
                // dipindah, atau dilepas sama sekali.
                event.stopPropagation();
                onMarkerMenu(event, marker.name);
              }}
              title={`${marker.name} — ${marker.md.toFixed(1)} m (${marker.source}). Right-click for the menu.`}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${fraction * 100}%`,
                height: 7,
                marginTop: -3,
                borderTop: `1.6px solid ${marker.color}`,
                cursor: "ns-resize",
                zIndex: 4,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function RenameMarkerDialog({
  panelWell,
  markerName,
  onClose,
  onSaved,
}: {
  panelWell: PanelWell;
  markerName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const marker = panelWell.markers.find((m) => m.name === markerName);
  const [name, setName] = useState(markerName);
  const [color, setColor] = useState(marker?.color ?? "#35b8c6");
  const [confidence, setConfidence] = useState(marker?.confidence ?? "high");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!marker) return;
    setBusy(true);
    try {
      await api.patch(`/api/markers/${marker.id}/`, { name, color, confidence });
      toast("Marker updated.");
      onSaved();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Markers — ${panelWell.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <TextField label="Marker name" value={name} onChange={setName} autoFocus />
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
        <TextField label="Colour (hex)" value={color} onChange={setColor} />
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: color,
            border: "1px solid var(--border-control)",
            flex: "none",
          }}
        />
      </div>
      <Dropdown
        value={confidence}
        label="Confidence"
        options={[
          { value: "high", label: "High" },
          { value: "medium", label: "Medium" },
          { value: "low", label: "Low" },
        ]}
        onChange={(value) => setConfidence(value as typeof confidence)}
        width={160}
      />
    </Modal>
  );
}
